// cspell:disable

import { ONE_DAY_MS } from "@redotech/util/date";
import { isEnumValue } from "@redotech/util/enum";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// The names of the carriers that we should be using in our system
export enum StandardCarrier {
  FEDEX = "FedEx",
  UPS = "UPS®",
  USPS = "USPS",
  DHLExpress = "DHLExpress",
  VEHO = "Veho",
  APC = "APC",
  AMAZON = "Amazon",
  DHL = "DHL eCommerce",
  GLS = "GLS",
  ASENDIA = "ASENDIA",
  SPEE_DEE = "SpeeDee",
  PASSPORT_GLOBAL = "PassportGlobal",
  LANDMARK_GLOBAL = "Landmark Global",
}

export const standardCarrierToLabel: Record<StandardCarrier, string> = {
  [StandardCarrier.FEDEX]: "FedEx",
  [StandardCarrier.UPS]: "UPS®",
  [StandardCarrier.USPS]: "USPS",
  [StandardCarrier.DHLExpress]: "DHL Express",
  [StandardCarrier.VEHO]: "Veho",
  [StandardCarrier.APC]: "APC",
  [StandardCarrier.AMAZON]: "Amazon",
  [StandardCarrier.DHL]: "DHL eCommerce",
  [StandardCarrier.GLS]: "GLS",
  [StandardCarrier.ASENDIA]: "Asendia",
  [StandardCarrier.SPEE_DEE]: "SpeeDee",
  [StandardCarrier.PASSPORT_GLOBAL]: "Passport Global",
  [StandardCarrier.LANDMARK_GLOBAL]: "Landmark Global",
};

// Snowflake stores the courierNameEN
// TODO: There are more carriers in Snowflake than in our standard list. Ask Andrew why
export const standardCarrierToCourierNameEN: Record<StandardCarrier, string> = {
  [StandardCarrier.FEDEX]: "FedEx",
  [StandardCarrier.UPS]: "UPS",
  [StandardCarrier.USPS]: "USPS",
  [StandardCarrier.DHLExpress]: "DHLExpress",
  [StandardCarrier.VEHO]: "Veho",
  [StandardCarrier.APC]: "APC",
  [StandardCarrier.AMAZON]: "Amazon Shipping + Amazon MCF",
  [StandardCarrier.DHL]: "DHLExpress", // Only DHLExpress is used in Snowflake, not DHL eCommerce
  [StandardCarrier.GLS]: "GLS (US)",
  [StandardCarrier.ASENDIA]: "AsendiaUsa",
  [StandardCarrier.SPEE_DEE]: "SpeeDee",
  [StandardCarrier.PASSPORT_GLOBAL]: "Passport Global",
  [StandardCarrier.LANDMARK_GLOBAL]: "Landmark Global",
};

// All of the carrier names that easypost gives us. Each carrier account can have a different name.
export enum Carriers {
  FEDEX = "FedEx",
  UPS = "UPS",
  UPS_SUREPOST = "UPSSurePost",
  USPS = "USPS",
  USPS_RETURNS = "USPSReturns",
  USPS_SHIP = "UspsShip",
  FEDEX_SMART_POST = "FedExSmartPost",
  AMAZON = "AmazonShipping",
  DHL_EXPRESS = "DHLExpress",
  DHL_ECS = "DhlEcs",
  VEHO = "Veho",
  APC = "APC",
  FIRST_MILE = "FirstMile",
  AUSTRALIA_POST = "AustraliaPost",
  CANADA_POST = "CanadaPost",
  GLS = "GSO",
  ASENDIA = "AsendiaUsa",
  ROYAL_MAIL_V3 = "RoyalMailV3",
  SPEE_DEE = "SpeeDee",
  PASSPORT_GLOBAL = "PassportGlobal",
  EPOST_GLOBAL = "EPostGlobal",
  LANDMARK_GLOBAL = "LandmarkGlobal", // Not a supported EasyPost carrier. Direct integration
  ETA_EXPRESS = "ETA Express", // Not a supported EasyPost carrier. Direct integration
  CROSSBORDERLY = "crossborderly", // Not EasyPost.
}

export const carrierToLabel: Record<Carriers, string> = {
  [Carriers.FEDEX]: "FedEx",
  [Carriers.FEDEX_SMART_POST]: "FedEx",
  [Carriers.UPS]: "UPS®",
  [Carriers.UPS_SUREPOST]: "UPS®",
  [Carriers.USPS]: "USPS",
  [Carriers.USPS_RETURNS]: "USPS",
  [Carriers.USPS_SHIP]: "USPS",
  [Carriers.AMAZON]: "Amazon",
  [Carriers.DHL_EXPRESS]: "DHL Express",
  [Carriers.DHL_ECS]: "DHL eCommerce",
  [Carriers.VEHO]: "Veho",
  [Carriers.APC]: "APC",
  [Carriers.FIRST_MILE]: "FirstMile",
  [Carriers.AUSTRALIA_POST]: "Australia Post",
  [Carriers.CANADA_POST]: "Canada Post",
  [Carriers.GLS]: "GLS",
  [Carriers.ASENDIA]: "Asendia",
  [Carriers.ROYAL_MAIL_V3]: "Royal Mail V3",
  [Carriers.SPEE_DEE]: "SpeeDee",
  [Carriers.PASSPORT_GLOBAL]: "Passport Global",
  [Carriers.EPOST_GLOBAL]: "ePost Global",
  [Carriers.LANDMARK_GLOBAL]: "Landmark Global",
  [Carriers.ETA_EXPRESS]: "ETA Express",
  [Carriers.CROSSBORDERLY]: "Crossborderly",
};

export function isCarrier(carrier: string): carrier is Carriers {
  return isEnumValue(carrier, Carriers);
}

export const standardCarrierToCarrier: Record<StandardCarrier, Carriers> = {
  [StandardCarrier.FEDEX]: Carriers.FEDEX,
  [StandardCarrier.UPS]: Carriers.UPS,
  [StandardCarrier.USPS]: Carriers.USPS,
  [StandardCarrier.DHLExpress]: Carriers.DHL_EXPRESS,
  [StandardCarrier.DHL]: Carriers.DHL_ECS,
  [StandardCarrier.VEHO]: Carriers.VEHO,
  [StandardCarrier.APC]: Carriers.APC,
  [StandardCarrier.AMAZON]: Carriers.AMAZON,
  [StandardCarrier.GLS]: Carriers.GLS,
  [StandardCarrier.ASENDIA]: Carriers.ASENDIA,
  [StandardCarrier.SPEE_DEE]: Carriers.SPEE_DEE,
  [StandardCarrier.PASSPORT_GLOBAL]: Carriers.PASSPORT_GLOBAL,
  [StandardCarrier.LANDMARK_GLOBAL]: Carriers.LANDMARK_GLOBAL,
};

export function getTrackingLink(
  carrier: Carriers | string | null | undefined,
  trackingCode: string,
  publicUrl: string,
  service?: string,
) {
  if (isEnumValue(carrier, Carriers)) {
    switch (carrier) {
      case Carriers.USPS:
      case Carriers.USPS_RETURNS:
      case Carriers.USPS_SHIP:
        // For USPS First Class mail, use the EasyPost tracking URL if available
        if (
          service &&
          [
            UspsServices.FirstClass,
            UspsServices.FirstClassMailInternational,
            UspsServices.FirstClassPackageInternationalService,
          ].includes(service as UspsServices) &&
          publicUrl
        ) {
          return publicUrl;
        }
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingCode}`;
      case Carriers.UPS:
      case Carriers.UPS_SUREPOST:
        return `https://www.ups.com/track?loc=en_US&tracknum=${trackingCode}`;
      case Carriers.FEDEX:
      case Carriers.FEDEX_SMART_POST:
        return `https://www.fedex.com/fedextrack/?trknbr=${trackingCode}`;
      case Carriers.AMAZON:
        return `https://track.amazon.com/tracking/${trackingCode}`;
      case Carriers.FIRST_MILE:
        return `https://tracking.firstmile.com/detail.php?n=${trackingCode}`;
      case Carriers.DHL_EXPRESS:
      case Carriers.DHL_ECS:
        return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingCode}`;
      case Carriers.VEHO:
        return publicUrl;
      case Carriers.APC:
        return publicUrl;
      case Carriers.AUSTRALIA_POST:
        return publicUrl;
      case Carriers.CANADA_POST:
        return publicUrl;
      case Carriers.GLS:
        return publicUrl;
      case Carriers.ASENDIA:
        return publicUrl;
      case Carriers.ROYAL_MAIL_V3:
        return publicUrl;
      case Carriers.SPEE_DEE:
        return publicUrl;
      case Carriers.PASSPORT_GLOBAL:
        return publicUrl;
      case Carriers.EPOST_GLOBAL:
        return publicUrl;
      case Carriers.LANDMARK_GLOBAL:
        return publicUrl;
      case Carriers.ETA_EXPRESS:
        return `https://v2.etaexpress.com/tracking/details.php?tracking_number=${trackingCode}`;
      case Carriers.CROSSBORDERLY:
        return "https://track.crossborderly.com";
      default:
        assertNever(carrier);
    }
  } else {
    return publicUrl;
  }
}

export function isUspsCarrier(carrier: string): boolean {
  const USPS_CARRIERS = [
    Carriers.USPS,
    Carriers.USPS_RETURNS,
    Carriers.USPS_SHIP,
  ];
  return isCarrier(carrier) && USPS_CARRIERS.includes(carrier);
}

export function isPoBoxEnabledCarrier(carrier: Carriers): boolean {
  const PO_BOX_ENABLED_CARRIERS = [
    Carriers.USPS,
    Carriers.USPS_RETURNS,
    Carriers.USPS_SHIP,
    Carriers.APC,
    Carriers.FIRST_MILE,
    Carriers.PASSPORT_GLOBAL,
    Carriers.LANDMARK_GLOBAL,
  ];
  return PO_BOX_ENABLED_CARRIERS.includes(carrier);
}

export function isAPOEnabledCarrier(carrier: Carriers) {
  const APO_ENABLED_CARRIERS = [
    Carriers.USPS,
    Carriers.USPS_RETURNS,
    Carriers.USPS_SHIP,
  ];
  return APO_ENABLED_CARRIERS.includes(carrier);
}

/**
 * Checks if a carrier is a FedEx carrier (includes both FedEx and FedExSmartPost)
 * Also has a fallback check for any carriers with "fedex" in their name
 */
export function isFedexCarrier(carrier: string): boolean {
  // First try the enum approach for known carriers
  const FEDEX_CARRIERS = [Carriers.FEDEX, Carriers.FEDEX_SMART_POST];

  // Primary check: see if it's a known FedEx carrier in our enum
  if (isCarrier(carrier) && FEDEX_CARRIERS.includes(carrier)) {
    return true;
  }

  // Fallback check: see if "fedex" appears anywhere in the carrier name
  return carrier.toLowerCase().includes("fedex");
}

export function isUpsCarrier(carrier: string): boolean {
  const UPS_CARRIERS = [Carriers.UPS, Carriers.UPS_SUREPOST];
  return isCarrier(carrier) && UPS_CARRIERS.includes(carrier);
}

/**
 * Includes both FedEx and FedExSmartPost
 */
export enum FedexServices {
  FEDEX_INTERNATIONAL_PRIORITY_EXPRESS = "FEDEX_INTERNATIONAL_PRIORITY_EXPRESS",
  FEDEX_INTERNATIONAL_CONNECT_PLUS = "FEDEX_INTERNATIONAL_CONNECT_PLUS",
  INTERNATIONAL_FIRST = "INTERNATIONAL_FIRST",
  FEDEX_GROUND = "FEDEX_GROUND",
  FEDEX_GROUND_HOME_DELIVERY = "GROUND_HOME_DELIVERY",
  FEDEX_2_DAY = "FEDEX_2_DAY",
  PRIORITY_OVERNIGHT = "PRIORITY_OVERNIGHT",
  FEDEX_FIRST_FREIGHT = "FEDEX_FIRST_FREIGHT",
  FEDEX_EXPRESS_SAVER = "FEDEX_EXPRESS_SAVER",
  FEDEX_3_DAY_FREIGHT = "FEDEX_3_DAY_FREIGHT",
  FEDEX_2_DAY_AM = "FEDEX_2_DAY_AM",
  FEDEX_2_DAY_FREIGHT = "FEDEX_2_DAY_FREIGHT",
  INTERNATIONAL_ECONOMY = "INTERNATIONAL_ECONOMY",
  FIRST_OVERNIGHT = "FIRST_OVERNIGHT",
  STANDARD_OVERNIGHT = "STANDARD_OVERNIGHT",
  SMART_POST = "SMART_POST",
  FEDEX_1_DAY_FREIGHT = "FEDEX_1_DAY_FREIGHT",
  INTERNATIONAL_PRIORITY = "INTERNATIONAL_PRIORITY",
  FEDEX_INTERNATIONAL_PRIORITY = "FEDEX_INTERNATIONAL_PRIORITY",
  EUROPE_FIRST_INTERNATIONAL_PRIORITY = "EUROPE_FIRST_INTERNATIONAL_PRIORITY",
  INTERNATIONAL_ECONOMY_FREIGHT = "INTERNATIONAL_ECONOMY_FREIGHT",
  INTERNATIONAL_PRIORITY_FREIGHT = "INTERNATIONAL_PRIORITY_FREIGHT",
}

export function isFedexService(
  service: FedexServices | UpsServices | UspsServices | string,
): service is FedexServices {
  return isEnumValue(service, FedexServices);
}

/**
 * https://www.fedex.com/en-us/shipping/saturday-and-sunday-delivery.html
 */
export function isValidFedexDeliveryDate({
  date,
  service,
}: {
  date: Temporal.ZonedDateTime;
  service: FedexServices;
}): boolean {
  switch (service) {
    case FedexServices.FEDEX_GROUND_HOME_DELIVERY:
      return true;
    default:
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
  }
}

/**
 * Includes both USPS and USPSReturns
 */
export enum UspsServices {
  GroundAdvantageReturn = "GroundAdvantageReturn",
  ExpressMailInternational = "ExpressMailInternational",
  PriorityMailInternational = "PriorityMailInternational",
  FirstClassPackageInternationalService = "FirstClassPackageInternationalService",
  GroundAdvantage = "GroundAdvantage",
  PriorityMailReturn = "PriorityMailReturn",
  Priority = "Priority",
  Express = "Express",
  FirstClass = "First",
  PriorityMailExpressReturn = "PriorityMailExpressReturn",
  MediaMail = "MediaMail",
  LibraryMail = "LibraryMail",
  FirstClassMailInternational = "FirstClassMailInternational",
}

export function isUspsService(
  service: FedexServices | UpsServices | UspsServices | string,
): service is UspsServices {
  return isEnumValue(service, UspsServices);
}

// https://support.easypost.com/hc/en-us/articles/360024353992-UPS-Supported-Services-and-Rating
export enum UpsServices {
  Ground = "Ground",
  NextDayAir = "NextDayAir",
  ThreeDaySelect = "3DaySelect",
  SecondDayAirAM = "2ndDayAirAM",
  NextDayAirEarlyAM = "NextDayAirEarlyAM",
  WorldWideSaver = "UPSSaver",
  WorldWideExpressPlus = "ExpressPlus",
  WorldWideExpress = "Express",
  WorldWideExpedited = "Expedited",
  NextDayAirSaver = "NextDayAirSaver",
  UPSStandard = "UPSStandard",
  SecondDayAir = "2ndDayAir",
  SurePostUnder1Lb = "SurePostUnder1Lb",
  SurePostOver1Lb = "SurePostOver1Lb",
  ExpressEarly = "ExpressEarly",
  ExpressSaver = "ExpressSaver",
}

export const upsInternationalServices: UpsServices[] = [
  UpsServices.WorldWideExpress,
  UpsServices.WorldWideExpedited,
  UpsServices.WorldWideExpressPlus,
  UpsServices.WorldWideSaver,
  UpsServices.UPSStandard,
] as const;

export function isUpsService(
  service: FedexServices | UpsServices | UspsServices | AmazonServices | string,
): service is UpsServices {
  return isEnumValue(service, UpsServices);
}

/**
 * https://www.ups.com/us/en/business-solutions/pickup-dropoff-options/weekend-pickups-deliveries
 */
export function isValidUpsDeliveryDate({
  date,
  service,
}: {
  date: Temporal.ZonedDateTime;
  service: UpsServices;
}): boolean {
  switch (service) {
    case UpsServices.Ground:
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    default:
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
  }
}

export enum AmazonServices {
  Ground = "Amazon Shipping Ground",
}

export function isAmazonService(
  service: FedexServices | UpsServices | UspsServices | AmazonServices | string,
): service is AmazonServices {
  return isEnumValue(service, AmazonServices);
}

// https://www.easypost.com/service-levels-and-parcels?carrier=dhlexpress
export enum DhlExpressServices {
  BreakBulkEconomy = "BreakBulkEconomy",
  BreakBulkExpress = "BreakBulkExpress",
  DomesticEconomySelect = "DomesticEconomySelect",
  DomesticExpress = "DomesticExpress",
  DomesticExpress1030 = "DomesticExpress1030",
  DomesticExpress1200 = "DomesticExpress1200",
  EconomySelect = "EconomySelect",
  EconomySelectNonDoc = "EconomySelectNonDoc",
  EuroPack = "EuroPack",
  EuropackNonDoc = "EuropackNonDoc",
  Express1030 = "Express1030",
  Express1030NonDoc = "Express1030NonDoc",
  Express1200NonDoc = "Express1200NonDoc",
  Express1200 = "Express1200",
  Express900 = "Express900",
  Express900NonDoc = "Express900NonDoc",
  ExpressEasy = "ExpressEasy",
  ExpressEasyNonDoc = "ExpressEasyNonDoc",
  ExpressEnvelope = "ExpressEnvelope",
  ExpressWorldwide = "ExpressWorldwide",
  ExpressWorldwideB2C = "ExpressWorldwideB2C",
  ExpressWorldwideB2CNonDoc = "ExpressWorldwideB2CNonDoc",
  ExpressWorldwideECX = "ExpressWorldwideECX",
  ExpressWorldwideNonDoc = "ExpressWorldwideNonDoc",
  FreightWorldwide = "FreightWorldwide",
  GlobalmailBusiness = "GlobalmailBusiness",
  JetLine = "JetLine",
  JumboBox = "JumboBox",
  LogisticsServices = "LogisticsServices",
  SameDay = "SameDay",
  SecureLine = "SecureLine",
  SprintLine = "SprintLine",
  MedicalExpressNonDoc = "MedicalExpressNonDoc",
}

export function isDhlExpressService(
  service: string,
): service is DhlExpressServices {
  return isEnumValue(service, DhlExpressServices);
}

export enum DhlEcsServices {
  DhlParcelExpedited = "DHLParcelExpedited",
  DhlParcelExpeditedMax = "DHLParcelExpeditedMax",
  DhlParcelGround = "DHLParcelGround",
  DhlParcelInternationalDirect = "DHLParcelInternationalDirect",
  DhlParcelInternationalStandard = "DHLParcelInternationalStandard",
  DhlPacketInternational = "DHLPacketInternational",
  DhlParcelInternationalDirectPriority = "DHLParcelInternationalDirectPriority",
  DhlParcelInternationalDirectStandard = "DHLParcelInternationalDirectStandard",
  DhlParcelInternationalDirectSMB = "DHLParcelInternationalDirectSMB",
  DhlParcelInternationalStandardSMB = "DHLParcelInternationalStandardSMB",
  DhlBPMExpedited = "DHLBPMExpedited",
  DhlBPMGround = "DHLBPMGround",
  DhlSmartMailParcelReturnGround = "DHLSmartMailParcelReturnGround",
  DhlSmartMailParcelReturnPlus = "DHLSmartMailParcelReturnPlus",
  DhlSmartMailParcelReturnLight = "DHLSmartMailParcelReturnLight",
}

export function isDhlEcsService(service: string): service is DhlEcsServices {
  return isEnumValue(service, DhlEcsServices);
}

export enum VehoServices {
  NextDay = "nextDay",
  SameDay = "sameDay",
}

export function isVehoService(
  service:
    | FedexServices
    | UpsServices
    | UspsServices
    | AmazonServices
    | VehoServices
    | string,
): service is VehoServices {
  return isEnumValue(service, VehoServices);
}

export enum ApcServices {
  ParcelConnectBookService = "parcelConnectBookService",
  ParcelConnectExpeditedDDP = "parcelConnectExpeditedDDP",
  ParcelConnectExpeditedDDU = "parcelConnectExpeditedDDU",
  ParcelConnectPriorityDDP = "parcelConnectPriorityDDP",
  ParcelConnectPriorityDDPDelcon = "parcelConnectPriorityDDPDelcon",
  ParcelConnectPriorityDDU = "parcelConnectPriorityDDU",
  ParcelConnectPriorityDDUDelcon = "parcelConnectPriorityDDUDelcon",
  ParcelConnectPriorityDDUPQW = "parcelConnectPriorityDDUPQW",
  ParcelConnectStandardDDU = "parcelConnectStandardDDU",
  ParcelConnectStandardDDUPQW = "parcelConnectStandardDDUPQW",
  ParcelConnectPacketDDU = "parcelConnectPacketDDU",
}

export function isApcService(
  service:
    | FedexServices
    | UpsServices
    | UspsServices
    | AmazonServices
    | VehoServices
    | ApcServices
    | string,
): service is ApcServices {
  return isEnumValue(service, ApcServices);
}

// https://docs.easypost.com/carriers/firstmile-guide#service-levels
export enum FirstMileServices {
  XParcelGround = "XParcelGround",
  XParcelPriority = "XParcelPriority",
  XParcelExpedited = "XParcelExpedited",
  XParcelExpeditedPlus = "XParcelExpeditedPlus",
}

export function isFirstMileService(
  service: FirstMileServices | string,
): service is FirstMileServices {
  return isEnumValue(service, FirstMileServices);
}

export enum AustraliaPostServices {
  ParcelPost = "ParcelPost",
  ExpressPost = "ExpressPost",
  International = "International",
  Commercial = "Commercial",
  ParcelPostSignature = "ParcelPostSignature" /** We don't know why, but the docs say "ParcelPost" but actual rates append "ParcelPostSignature" */,
  ExpressPostSignature = "ExpressPostSignature",
  InternationalSignature = "InternationalSignature",
  CommercialSignature = "CommercialSignature",
  EParcel1 = "EParcel1",
  EParcelPostReturns = "EParcelPostReturns",
  ExpressParcelPostReturns = "ExpressParcelPostReturns",
  ExpressPostEParcel = "ExpressPostEParcel",
  InternationalEconomyAirmailParcels = "InternationalEconomyAirmailParcels",
  InternationalEconomyWSODREGPost = "InternationalEconomyWSODREGPost",
  InternationalExpressDocSecIDocs = "InternationalExpressDocSecIDocs",
  InternationalExpressMerchandise = "InternationalExpressMerchandise",
  InternationalExpressMerchECIMerch = "InternationalExpressMerchECIMerch",
  InternationalStandardPackTrack = "InternationalStandardPackTrack",
  ParcelPostExtra = "ParcelPostExtra",
  ParcelPostWinePlusSignature = "ParcelPostWinePlusSignature",
  ParcelPostWineSignature = "ParcelPostWineSignature",
  ExpressEparcelPostReturns = "ExpressEparcelPostReturns",
  EparcelPostReturns = "EparcelPostReturns",
  IntlExpressDocs = "IntlExpressDocs",
  IntlStandardPackTrack = "IntlStandardPackTrack",
  IntLStandardWithSignature = "IntLStandardWithSignature", // The capital L is not a mistake, that's what we get
  IntlExpressMerch = "IntlExpressMerch",
}

export function isAustraliaPostService(
  service: AustraliaPostServices | string,
): service is AustraliaPostServices {
  return isEnumValue(service, AustraliaPostServices);
}

export enum CanadaPostServices {
  RegularParcel = "RegularParcel",
  ExpeditedParcel = "ExpeditedParcel",
  Xpresspost = "Xpresspost",
  Priority = "Priority",
  ExpeditedParcelUSA = "ExpeditedParcelUSA",
  SmallPacketUSAAir = "SmallPacketUSAAir",
  TrackedPacketUSA = "TrackedPacketUSA",
  TrackedPacketUSALVM = "TrackedPacketUSALVM",
  XpresspostUSA = "XpresspostUSA",
  XpresspostInternational = "XpresspostInternational",
  InternationalParcelAir = "InternationalParcelAir",
  InternationalParcelSurface = "InternationalParcelSurface",
  SmallPacketInternationalAir = "SmallPacketInternationalAir",
  SmallPacketInternationalSurface = "SmallPacketInternationalSurface",
  TrackedPacketInternational = "TrackedPacketInternational",
  ExpeditedParcelPlus = "ExpeditedParcelPlus",
}

export function isCanadaPostService(
  service: CanadaPostServices | string,
): service is CanadaPostServices {
  return isEnumValue(service, CanadaPostServices);
}

export enum GlsServices {
  EarlyPriorityOvernight = "EarlyPriorityOvernight",
  PriorityOvernight = "PriorityOvernight",
  CaliforniaParcelService = "CaliforniaParcelService",
  SaturdayDeliveryService = "SaturdayDeliveryService",
  EarlySaturdayService = "EarlySaturdayService",
  NoonPriorityService = "NoonPriorityService",
}

export function isGlsService(service: string): service is GlsServices {
  return isEnumValue(service, GlsServices);
}
export enum RoyalMailV3Services {
  RoyalMailV3 = "RoyalMailV3",
  DEImportRoyalMail24PlusParcelsWeekendService = "DEImportRoyalMail24PlusParcelsWeekendService",
  DEImportStandard24LargeLetter = "DEImportStandard24LargeLetter",
  DEImportStandard24LargeLetterWeekendService = "DEImportStandard24LargeLetterWeekendService",
  DEImportStandard24Parcel = "DEImportStandard24Parcel",
  DEImportStandard24ParcelWeekendService = "DEImportStandard24ParcelWeekendService",
  DEImportStandard48LargeLetter = "DEImportStandard48LargeLetter",
  DEImportStandard48LargeLetterWeekendService = "DEImportStandard48LargeLetterWeekendService",
  DEImportTracked24ParcelBoxableHighVolumeWeekendService = "DEImportTracked24ParcelBoxableHighVolumeWeekendService",
  DEImportTracked24ParcelBoxableWeekendService = "DEImportTracked24ParcelBoxableWeekendService",
  DEImportTracked24ParcelHighVolumeWeekendService = "DEImportTracked24ParcelHighVolumeWeekendService",
  DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE",
  DEImportTracked48ParcelBoxableHighVolumeWeekendService = "DEImportTracked48ParcelBoxableHighVolumeWeekendService",
  DEImportTracked48ParcelBoxableWeekendService = "DEImportTracked48ParcelBoxableWeekendService",
  DEImportTracked48ParcelHighVolumeWeekendService = "DEImportTracked48ParcelHighVolumeWeekendService",
  DEImportTracked48SignatureParcelHighVolumeAGE = "DEImportTracked48SignatureParcelHighVolumeAGE",
  DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE",
  DEImporttoEUMaxSortLargeLetter = "DEImporttoEUMaxSortLargeLetter",
  DEImporttoEUMaxSortParcel = "DEImporttoEUMaxSortParcel",
  DEImporttoEUSignedParcel = "DEImporttoEUSignedParcel",
  DEImporttoEUTrackedHighVolLargeLetter = "DEImporttoEUTrackedHighVolLargeLetter",
  DEImporttoEUTrackedParcel = "DEImporttoEUTrackedParcel",
  DEImporttoEUTrackedSignedLargeLetter = "DEImporttoEUTrackedSignedLargeLetter",
  DEImporttoEUTrackedSignedParcel = "DEImporttoEUTrackedSignedParcel",
  HMForcesMail = "HMForcesMail",
  HMForcesSignedFor = "HMForcesSignedFor",
  HMForcesSpecialDelivery1000 = "HMForcesSpecialDelivery1000",
  HMForcesSpecialDelivery2500 = "HMForcesSpecialDelivery2500",
  HMForcesSpecialDelivery500 = "HMForcesSpecialDelivery500",
  ImportDETracked24 = "ImportDETracked24",
  ImportDETracked24HighVolume = "ImportDETracked24HighVolume",
  ImportDETracked24Letterboxable = "ImportDETracked24Letterboxable",
  ImportDETracked24LetterboxableHighVolume = "ImportDETracked24LetterboxableHighVolume",
  ImportDETracked24SignatureHighVolumeAGE = "ImportDETracked24SignatureHighVolumeAGE",
  ImportDETracked24SignatureStandardAGE = "ImportDETracked24SignatureStandardAGE",
  ImportDETracked48 = "ImportDETracked48",
  ImportDETracked48HighVolume = "ImportDETracked48HighVolume",
  ImportDETracked48Letterboxable = "ImportDETracked48Letterboxable",
  ImportDETracked48LetterboxableHighVolume = "ImportDETracked48LetterboxableHighVolume",
  ImportDETrackedReturns24 = "ImportDETrackedReturns24",
  ImportDETrackedReturns48 = "ImportDETrackedReturns48",
  InternationalBusinessMailLargeLetterCountryPricedPriority = "InternationalBusinessMailLargeLetterCountryPricedPriority",
  InternationalBusinessMailLargeLetterCountryPricedPriorityETOE = "InternationalBusinessMailLargeLetterCountryPricedPriorityETOE",
  InternationalBusinessMailLargeLetterCountrySortPriority = "InternationalBusinessMailLargeLetterCountrySortPriority",
  InternationalBusinessMailLargeLetterMaxSortPriorityService = "InternationalBusinessMailLargeLetterMaxSortPriorityService",
  InternationalBusinessMailLargeLetterMaxSortResidueStandard = "InternationalBusinessMailLargeLetterMaxSortResidueStandard",
  InternationalBusinessMailLargeLetterMaxSortStandardService = "InternationalBusinessMailLargeLetterMaxSortStandardService",
  InternationalBusinessMailLargeLetterSemiTracked = "InternationalBusinessMailLargeLetterSemiTracked",
  InternationalBusinessMailLargeLetterZeroSortPriority = "InternationalBusinessMailLargeLetterZeroSortPriority",
  InternationalBusinessMailLargeLetterZeroSortPriorityMachine = "InternationalBusinessMailLargeLetterZeroSortPriorityMachine",
  InternationalBusinessMailLargeLetterZoneSortPriority = "InternationalBusinessMailLargeLetterZoneSortPriority",
  InternationalBusinessMailLargeLetterZoneSortPriorityMachine = "InternationalBusinessMailLargeLetterZoneSortPriorityMachine",
  InternationalBusinessMailLetterMaxSortResidueStandard = "InternationalBusinessMailLetterMaxSortResidueStandard",
  InternationalBusinessMailLettersMaxSortStandard = "InternationalBusinessMailLettersMaxSortStandard",
  InternationalBusinessMailLettersZeroSortPriority = "InternationalBusinessMailLettersZeroSortPriority",
  InternationalBusinessMailLettersZoneSortPriority = "InternationalBusinessMailLettersZoneSortPriority",
  InternationalBusinessMailSignedCountryPriced = "InternationalBusinessMailSignedCountryPriced",
  InternationalBusinessMailSignedExtraCompensationCountryPriced = "InternationalBusinessMailSignedExtraCompensationCountryPriced",
  InternationalBusinessMailSignedHighVolCountryPriced = "InternationalBusinessMailSignedHighVolCountryPriced",
  InternationalBusinessMailSignedZoneSort = "InternationalBusinessMailSignedZoneSort",
  InternationalBusinessMailTrackedCountryPriced = "InternationalBusinessMailTrackedCountryPriced",
  InternationalBusinessMailTrackedHighVolCountryPriced = "InternationalBusinessMailTrackedHighVolCountryPriced",
  InternationalBusinessMailTrackedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedCountryPriced = "InternationalBusinessMailTrackedSignedCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolCountryPriced = "InternationalBusinessMailTrackedSignedHighVolCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedZoneSort = "InternationalBusinessMailTrackedSignedZoneSort",
  InternationalBusinessMailTrackedZoneSort = "InternationalBusinessMailTrackedZoneSort",
  InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE = "InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE",
  InternationalBusinessParcelPriorityCountryPricedBoxable = "InternationalBusinessParcelPriorityCountryPricedBoxable",
  InternationalBusinessParcelPriorityCountryPricedBoxableDDP = "InternationalBusinessParcelPriorityCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPriced = "InternationalBusinessParcelTrackedCountryPriced",
  InternationalBusinessParcelTrackedCountryPricedBoxable = "InternationalBusinessParcelTrackedCountryPricedBoxable",
  InternationalBusinessParcelTrackedCountryPricedBoxableDDP = "InternationalBusinessParcelTrackedCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp = "InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp",
  InternationalBusinessParcels = "InternationalBusinessParcels",
  InternationalBusinessParcelsPrintDirectPriority = "InternationalBusinessParcelsPrintDirectPriority",
  InternationalBusinessParcelsPrintDirectStandard = "InternationalBusinessParcelsPrintDirectStandard",
  InternationalBusinessParcelsSignedCountryPriced = "InternationalBusinessParcelsSignedCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationZoneSort = "InternationalBusinessParcelsSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsSignedZoneSort = "InternationalBusinessParcelsSignedZoneSort",
  InternationalBusinessParcelsTrackedCountryPriced = "InternationalBusinessParcelsTrackedCountryPriced",
  InternationalBusinessParcelsTrackedCountryPricedETOE = "InternationalBusinessParcelsTrackedCountryPricedETOE",
  InternationalBusinessParcelsTrackedDirectIrelandCountry = "InternationalBusinessParcelsTrackedDirectIrelandCountry",
  InternationalBusinessParcelsTrackedExtraCompCountryPriced = "InternationalBusinessParcelsTrackedExtraCompCountryPriced",
  InternationalBusinessParcelsTrackedExtraCompZoneSort = "InternationalBusinessParcelsTrackedExtraCompZoneSort",
  InternationalBusinessParcelsTrackedSignedCountryPriced = "InternationalBusinessParcelsTrackedSignedCountryPriced",
  InternationalBusinessParcelsTrackedSignedCountryPricedETOE = "InternationalBusinessParcelsTrackedSignedCountryPricedETOE",
  InternationalBusinessParcelsTrackedSignedDDP = "InternationalBusinessParcelsTrackedSignedDDP",
  InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort = "InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsTrackedSignedZoneSort = "InternationalBusinessParcelsTrackedSignedZoneSort",
  InternationalBusinessParcelsTrackedZoneSort = "InternationalBusinessParcelsTrackedZoneSort",
  InternationalBusinessParcelsZeroSortPriority = "InternationalBusinessParcelsZeroSortPriority",
  InternationalBusinessParcelsZeroSortPriorityETOE = "InternationalBusinessParcelsZeroSortPriorityETOE",
  InternationalBusinessParcelsZoneSortPriorityService = "InternationalBusinessParcelsZoneSortPriorityService",
  InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE = "InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceMaxSortLetter = "InternationalBusinessPersonalCorrespondenceMaxSortLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter",
  InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE",
  InternationalBusinessPrintedMatterPacket = "InternationalBusinessPrintedMatterPacket",
  InternationalBusinessTrackedExpressNPC = "InternationalBusinessTrackedExpressNPC",
  InternationalEconomyOnAccount = "InternationalEconomyOnAccount",
  InternationalSignedOnAccount = "InternationalSignedOnAccount",
  InternationalSignedOnAccountExtraComp = "InternationalSignedOnAccountExtraComp",
  InternationalStandardOnAccount = "InternationalStandardOnAccount",
  InternationalTrackedOnAccount = "InternationalTrackedOnAccount",
  InternationalTrackedOnAccountExtraComp = "InternationalTrackedOnAccountExtraComp",
  InternationalTrackedParcels30kg = "InternationalTrackedParcels30kg",
  InternationalTrackedParcels30kgCPrio = "InternationalTrackedParcels30kgCPrio",
  InternationalTrackedParcels30kgETOEC = "InternationalTrackedParcels30kgETOEC",
  InternationalTrackedParcels30kgETOEE = "InternationalTrackedParcels30kgETOEE",
  InternationalTrackedParcels30kgExtraComp = "InternationalTrackedParcels30kgExtraComp",
  InternationalTrackedParcels30kgExtraCompETOEC = "InternationalTrackedParcels30kgExtraCompETOEC",
  InternationalTrackedParcels30kgExtraCompETOEE = "InternationalTrackedParcels30kgExtraCompETOEE",
  InternationalTrackedParcels30kgXCompCPrio = "InternationalTrackedParcels30kgXCompCPrio",
  InternationalTrackedSignedOnAccount = "InternationalTrackedSignedOnAccount",
  InternationalTrackedSignedOnAccountExtraComp = "InternationalTrackedSignedOnAccountExtraComp",
  ParcelpostFlatRateAnnual = "ParcelpostFlatRateAnnual",
  RM24LargeLetterAnnualFlatRate = "RM24LargeLetterAnnualFlatRate",
  RM24PresortedLargeLetterAnnualFlatRate = "RM24PresortedLargeLetterAnnualFlatRate",
  RM24PresortedParcelsAnnualFlatRate = "RM24PresortedParcelsAnnualFlatRate",
  RM48LargeLetterAnnualFlatRate = "RM48LargeLetterAnnualFlatRate",
  RM48PresortedLargeLetterAnnualFlatRate = "RM48PresortedLargeLetterAnnualFlatRate",
  RM48PresortedParcelsAnnualFlatRate = "RM48PresortedParcelsAnnualFlatRate",
  RoyalMail1stClass = "RoyalMail1stClass",
  RoyalMail1stClassLettersDailyRateservice = "RoyalMail1stClassLettersDailyRateservice",
  RoyalMail1stClassSignedFor = "RoyalMail1stClassSignedFor",
  RoyalMail24LargeLetterDailyRate = "RoyalMail24LargeLetterDailyRate",
  RoyalMail24LargeLetterFlatRate = "RoyalMail24LargeLetterFlatRate",
  RoyalMail24LargeLetterFlatRateService = "RoyalMail24LargeLetterFlatRateService",
  RoyalMail24PacketpostFlatRateService = "RoyalMail24PacketpostFlatRateService",
  RoyalMail24ParcelDailyRateService = "RoyalMail24ParcelDailyRateService",
  RoyalMail24ParcelSort8DailyRateService = "RoyalMail24ParcelSort8DailyRateService",
  RoyalMail24ParcelSort8FlatRateService = "RoyalMail24ParcelSort8FlatRateService",
  RoyalMail24ParcelsDailyRate = "RoyalMail24ParcelsDailyRate",
  RoyalMail24ParcelsFlatRate = "RoyalMail24ParcelsFlatRate",
  RoyalMail24PresortedLargeLetter = "RoyalMail24PresortedLargeLetter",
  RoyalMail24PresortedParcels = "RoyalMail24PresortedParcels",
  RoyalMail24SORT8LargeLetterAnnualFlatRate = "RoyalMail24SORT8LargeLetterAnnualFlatRate",
  RoyalMail24SORT8ParcelsAnnualFlatRate = "RoyalMail24SORT8ParcelsAnnualFlatRate",
  RoyalMail2ndClass = "RoyalMail2ndClass",
  RoyalMail2ndClassLettersDailyRateservice = "RoyalMail2ndClassLettersDailyRateservice",
  RoyalMail2ndClassSignedFor = "RoyalMail2ndClassSignedFor",
  RoyalMail48LargeLetterDailyRate = "RoyalMail48LargeLetterDailyRate",
  RoyalMail48LargeLetterFlatRate = "RoyalMail48LargeLetterFlatRate",
  RoyalMail48LargeLetterFlatRateService = "RoyalMail48LargeLetterFlatRateService",
  RoyalMail48PacketpostFlatRateService = "RoyalMail48PacketpostFlatRateService",
  RoyalMail48ParcelDailyRateService = "RoyalMail48ParcelDailyRateService",
  RoyalMail48ParcelSort8DailyRateService = "RoyalMail48ParcelSort8DailyRateService",
  RoyalMail48ParcelSort8FlatRateService = "RoyalMail48ParcelSort8FlatRateService",
  RoyalMail48ParcelsDailyRate = "RoyalMail48ParcelsDailyRate",
  RoyalMail48ParcelsFlatRate = "RoyalMail48ParcelsFlatRate",
  RoyalMail48PresortedLargeLetter = "RoyalMail48PresortedLargeLetter",
  RoyalMail48PresortedParcels = "RoyalMail48PresortedParcels",
  RoyalMail48SORT8LargeLetterAnnualFlatRate = "RoyalMail48SORT8LargeLetterAnnualFlatRate",
  RoyalMail48Sort8ParcelsAnnualFlatRate = "RoyalMail48Sort8ParcelsAnnualFlatRate",
  SpecialDeliveryGuaranteed1000 = "SpecialDeliveryGuaranteed1000",
  SpecialDeliveryGuaranteed2500 = "SpecialDeliveryGuaranteed2500",
  SpecialDeliveryGuaranteed750 = "SpecialDeliveryGuaranteed750",
  SpecialDeliveryGuaranteedAGE1000 = "SpecialDeliveryGuaranteedAGE1000",
  SpecialDeliveryGuaranteedAGE2500 = "SpecialDeliveryGuaranteedAGE2500",
  SpecialDeliveryGuaranteedAGE750 = "SpecialDeliveryGuaranteedAGE750",
  SpecialDeliveryGuaranteedBy1PM1000 = "SpecialDeliveryGuaranteedBy1PM1000",
  SpecialDeliveryGuaranteedBy1PM2500 = "SpecialDeliveryGuaranteedBy1PM2500",
  SpecialDeliveryGuaranteedBy1PM750 = "SpecialDeliveryGuaranteedBy1PM750",
  SpecialDeliveryGuaranteedBy1PMAGE1000 = "SpecialDeliveryGuaranteedBy1PMAGE1000",
  SpecialDeliveryGuaranteedBy1PMAGE2500 = "SpecialDeliveryGuaranteedBy1PMAGE2500",
  SpecialDeliveryGuaranteedBy1PMAGE750 = "SpecialDeliveryGuaranteedBy1PMAGE750",
  SpecialDeliveryGuaranteedBy1PMID1000 = "SpecialDeliveryGuaranteedBy1PMID1000",
  SpecialDeliveryGuaranteedBy1PMID2500 = "SpecialDeliveryGuaranteedBy1PMID2500",
  SpecialDeliveryGuaranteedBy1PMID750 = "SpecialDeliveryGuaranteedBy1PMID750",
  SpecialDeliveryGuaranteedBy9AM1000 = "SpecialDeliveryGuaranteedBy9AM1000",
  SpecialDeliveryGuaranteedBy9AM2500 = "SpecialDeliveryGuaranteedBy9AM2500",
  SpecialDeliveryGuaranteedBy9AM750 = "SpecialDeliveryGuaranteedBy9AM750",
  SpecialDeliveryGuaranteedBy9AMAGE1000 = "SpecialDeliveryGuaranteedBy9AMAGE1000",
  SpecialDeliveryGuaranteedBy9AMAGE2500 = "SpecialDeliveryGuaranteedBy9AMAGE2500",
  SpecialDeliveryGuaranteedBy9AMAGE750 = "SpecialDeliveryGuaranteedBy9AMAGE750",
  SpecialDeliveryGuaranteedBy9AMID1000 = "SpecialDeliveryGuaranteedBy9AMID1000",
  SpecialDeliveryGuaranteedBy9AMID2500 = "SpecialDeliveryGuaranteedBy9AMID2500",
  SpecialDeliveryGuaranteedBy9AMID750 = "SpecialDeliveryGuaranteedBy9AMID750",
  SpecialDeliveryGuaranteedID1000 = "SpecialDeliveryGuaranteedID1000",
  SpecialDeliveryGuaranteedID2500 = "SpecialDeliveryGuaranteedID2500",
  SpecialDeliveryGuaranteedID750 = "SpecialDeliveryGuaranteedID750",
  Tracked24 = "Tracked24",
  Tracked24HighVolume = "Tracked24HighVolume",
  Tracked24HighVolumeSignatureAGE = "Tracked24HighVolumeSignatureAGE",
  Tracked24SignatureAGE = "Tracked24SignatureAGE",
  Tracked48 = "Tracked48",
  Tracked48HighVolume = "Tracked48HighVolume",
  Tracked48HighVolumeSignatureAGE = "Tracked48HighVolumeSignatureAGE",
  Tracked48SignatureAGE = "Tracked48SignatureAGE",
  TrackedLetterBoxable24 = "TrackedLetterBoxable24",
  TrackedLetterBoxable24HighVolume = "TrackedLetterBoxable24HighVolume",
  TrackedLetterBoxable48 = "TrackedLetterBoxable48",
  TrackedLetterBoxable48HighVolume = "TrackedLetterBoxable48HighVolume",
  TrackedReturns24 = "TrackedReturns24",
  TrackedReturns48 = "TrackedReturns48",
}

export function isRoyalMailV3Service(
  service: RoyalMailV3Services | string,
): service is RoyalMailV3Services {
  return isEnumValue(service, RoyalMailV3Services);
}

export enum AsendiaServices {
  ADS = "ADS",
  AirFreightInbound = "AirFreightInbound",
  AirFreightOutbound = "AirFreightOutbound",
  AsendiaDomesticBoundPrinterMatterExpedited = "AsendiaDomesticBoundPrinterMatterExpedited",
  AsendiaDomesticBoundPrinterMatterGround = "AsendiaDomesticBoundPrinterMatterGround",
  AsendiaDomesticFlatsExpedited = "AsendiaDomesticFlatsExpedited",
  AsendiaDomesticFlatsGround = "AsendiaDomesticFlatsGround",
  AsendiaDomesticParcelGroundOver1lb = "AsendiaDomesticParcelGroundOver1lb",
  AsendiaDomesticParcelGroundUnder1lb = "AsendiaDomesticParcelGroundUnder1lb",
  AsendiaDomesticParcelMAXOver1lb = "AsendiaDomesticParcelMAXOver1lb",
  AsendiaDomesticParcelMAXUnder1lb = "AsendiaDomesticParcelMAXUnder1lb",
  AsendiaDomesticParcelOver1lbExpedited = "AsendiaDomesticParcelOver1lbExpedited",
  AsendiaDomesticParcelUnder1lbExpedited = "AsendiaDomesticParcelUnder1lbExpedited",
  AsendiaDomesticPromoParcelExpedited = "AsendiaDomesticPromoParcelExpedited",
  AsendiaDomesticPromoParcelGround = "AsendiaDomesticPromoParcelGround",
  BulkFreight = "BulkFreight",
  BusinessMailCanadaLettermail = "BusinessMailCanadaLettermail",
  BusinessMailCanadaLettermailMachineable = "BusinessMailCanadaLettermailMachineable",
  BusinessMailEconomy = "BusinessMailEconomy",
  BusinessMailEconomyLPWholesale = "BusinessMailEconomyLPWholesale",
  BusinessMailEconomySPWholesale = "BusinessMailEconomySPWholesale",
  BusinessMailIPA = "BusinessMailIPA",
  BusinessMailISAL = "BusinessMailISAL",
  BusinessMailPriority = "BusinessMailPriority",
  BusinessMailPriorityLPWholesale = "BusinessMailPriorityLPWholesale",
  BusinessMailPrioritySPWholesale = "BusinessMailPrioritySPWholesale",
  MarketingMailCanadaPersonalizedLCP = "MarketingMailCanadaPersonalizedLCP",
  MarketingMailCanadaPersonalizedMachineable = "MarketingMailCanadaPersonalizedMachineable",
  MarketingMailCanadaPersonalizedNDG = "MarketingMailCanadaPersonalizedNDG",
  MarketingMailEconomy = "MarketingMailEconomy",
  MarketingMailIPA = "MarketingMailIPA",
  MarketingMailISAL = "MarketingMailISAL",
  MarketingMailPriority = "MarketingMailPriority",
  PublicationsCanadaLCP = "PublicationsCanadaLCP",
  PublicationsCanadaNDG = "PublicationsCanadaNDG",
  PublicationsEconomy = "PublicationsEconomy",
  PublicationsIPA = "PublicationsIPA",
  PublicationsISAL = "PublicationsISAL",
  PublicationsPriority = "PublicationsPriority",
  ePAQElite = "ePAQElite",
  ePAQEliteCustom = "ePAQEliteCustom",
  ePAQEliteDAP = "ePAQEliteDAP",
  ePAQEliteDDP = "ePAQEliteDDP",
  ePAQEliteDDPOversized = "ePAQEliteDDPOversized",
  ePAQEliteDPD = "ePAQEliteDPD",
  ePAQEliteDirectAccessCanadaDDP = "ePAQEliteDirectAccessCanadaDDP",
  ePAQEliteOversized = "ePAQEliteOversized",
  ePAQPlus = "ePAQPlus",
  ePAQPlusCustom = "ePAQPlusCustom",
  ePAQPlusCustomsPrepaid = "ePAQPlusCustomsPrepaid",
  ePAQPlusDAP = "ePAQPlusDAP",
  ePAQPlusDDP = "ePAQPlusDDP",
  ePAQPlusEconomy = "ePAQPlusEconomy",
  ePAQPlusWholesale = "ePAQPlusWholesale",
  ePAQPlusePacket = "ePAQPlusePacket",
  ePAQPlusePacketCanadaCustomsPrePaid = "ePAQPlusePacketCanadaCustomsPrePaid",
  ePAQPlusePacketCanadaDDP = "ePAQPlusePacketCanadaDDP",
  ePAQReturnsDomestic = "ePAQReturnsDomestic",
  ePAQReturnsInternational = "ePAQReturnsInternational",
  ePAQSelect = "ePAQSelect",
  ePAQSelectCustom = "ePAQSelectCustom",
  ePAQSelectCustomsPrepaidByShopper = "ePAQSelectCustomsPrepaidByShopper",
  ePAQSelectDAP = "ePAQSelectDAP",
  ePAQSelectDDP = "ePAQSelectDDP",
  ePAQSelectDDPDirectAccess = "ePAQSelectDDPDirectAccess",
  ePAQSelectDirectAccess = "ePAQSelectDirectAccess",
  ePAQSelectDirectAccessCanadaDDP = "ePAQSelectDirectAccessCanadaDDP",
  ePAQSelectEconomy = "ePAQSelectEconomy",
  ePAQSelectOversized = "ePAQSelectOversized",
  ePAQSelectOversizedDDP = "ePAQSelectOversizedDDP",
  ePAQSelectPMEI = "ePAQSelectPMEI",
  ePAQSelectPMEICanadaCustomsPrePaid = "ePAQSelectPMEICanadaCustomsPrePaid",
  ePAQSelectPMEIPCPostage = "ePAQSelectPMEIPCPostage",
  ePAQSelectPMI = "ePAQSelectPMI",
  ePAQSelectPMICanadaCustomsPrepaid = "ePAQSelectPMICanadaCustomsPrepaid",
  ePAQSelectPMICanadaDDP = "ePAQSelectPMICanadaDDP",
  ePAQSelectPMINonPresort = "ePAQSelectPMINonPresort",
  ePAQSelectPMIPCPostage = "ePAQSelectPMIPCPostage",
  ePAQStandard = "ePAQStandard",
  ePAQStandardCustom = "ePAQStandardCustom",
  ePAQStandardEconomy = "ePAQStandardEconomy",
  ePAQStandardIPA = "ePAQStandardIPA",
  ePAQStandardISAL = "ePAQStandardISAL",
  ePaqSelectPMEINonPresort = "ePaqSelectPMEINonPresort",
  ePAQSelectDDPDG = "ePAQSelectDDPDG",
  AsendiaDomesticParcelMaxUnder70lb = "AsendiaDomesticParcelMaxUnder70lb",
}

export enum PassportGlobalServices {
  PriorityDdpDelcon = "PriorityDdpDelcon",
  PriorityDdp = "PriorityDdp",
  epacketDdp = "epacketDdp",
  ExpressDdp = "ExpressDdp",
  PriorityDduDelcon = "PriorityDduDelcon",
  PriorityDdu = "PriorityDdu",
  ExpeditedDdpDelcon = "ExpeditedDdpDelcon",
  ExpressDdpDelcon = "ExpressDdpDelcon",
  GlobalPlus = "GlobalPlus",
}

export function isPassportGlobalService(
  service: PassportGlobalServices | string,
): service is PassportGlobalServices {
  return isEnumValue(service, PassportGlobalServices);
}

export enum EPostGlobalServices {
  CourierService = "CourierService",
  CourierServiceDutiesPaid = "CourierServiceDutiesPaid",
  DomesticPriorityParcel = "DomesticPriorityParcel",
  DomesticeDGE = "DomesticeDGE",
  EconomyParcel = "EconomyParcel",
  ExpeditedParcelWithDeliveryConfirmationDutiesPaid = "ExpeditedParcelWithDeliveryConfirmationDutiesPaid",
  ExpeditedWithDC = "ExpeditedWithDC",
  FirstClassParcelsInternational = "FirstClassParcelsInternational",
  IPA = "IPA",
  ISAL = "ISAL",
  PMEI = "PMEI",
  PMEIDDP = "PMEIDDP",
  PMIPresortService = "PMIPresortService",
  PMIServiceDutyPaid = "PMIServiceDutyPaid",
  PriorityPacketDDU = "PriorityPacketDDU",
  PriorityParcelNoDeliveryConfirmation = "PriorityParcelNoDeliveryConfirmation",
  PriorityParcelNoDeliveryConfirmationDutiesPaid = "PriorityParcelNoDeliveryConfirmationDutiesPaid",
  PriorityParcelWithDeliveryConfirmation = "PriorityParcelWithDeliveryConfirmation",
  PriorityParcelWithDeliveryConfirmationDutiesPaid = "PriorityParcelWithDeliveryConfirmationDutiesPaid",
  ePacket = "ePacket",
  ePacketDDP = "ePacketDDP",
}

export function isEPostGlobalService(
  service: EPostGlobalServices | string,
): service is EPostGlobalServices {
  return isEnumValue(service, EPostGlobalServices);
}

export function isAsendiaService(service: string): service is AsendiaServices {
  return isEnumValue(service, AsendiaServices);
}

export enum SpeeDeeServices {
  SpeeDeeDelivery = "SpeeDeeDelivery",
}

export function isSpeeDeeService(service: string): service is SpeeDeeServices {
  return isEnumValue(service, SpeeDeeServices);
}

export enum CrossborderlyServices {
  Crossborderly = "Crossborderly",
}

export function isCrossborderlyService(
  service: CrossborderlyServices | string,
): service is CrossborderlyServices {
  return isEnumValue(service, CrossborderlyServices);
}

export enum ETAExpressServices {
  ETAExpress = "ETAExpress",
}

export function isETAExpressService(
  service: ETAExpressServices | string,
): service is ETAExpressServices {
  return isEnumValue(service, ETAExpressServices);
}

export enum LandmarkGlobalServices {
  Delivery = "Delivery", // Generic service for Landmark Global
}

export function isLandmarkGlobalService(
  service: string,
): service is LandmarkGlobalServices {
  return isEnumValue(service, LandmarkGlobalServices);
}

export enum ServiceLevel {
  STANDARD = "Standard",
  PLUS = "Plus",
  EXPRESS = "Express",
}

export const MAX_EXPRESS_DELIVERY_DAYS = 2;
export const MAX_PLUS_DELIVERY_DAYS = 4;
export const MAX_STANDARD_DELIVERY_DAYS = 7;

export const MAX_SERVICE_LEVEL_DAYS: Record<ServiceLevel, number> = {
  [ServiceLevel.STANDARD]: MAX_STANDARD_DELIVERY_DAYS,
  [ServiceLevel.PLUS]: MAX_PLUS_DELIVERY_DAYS,
  [ServiceLevel.EXPRESS]: MAX_EXPRESS_DELIVERY_DAYS,
};

const fedexServiceToServiceLevel: Record<FedexServices, ServiceLevel> = {
  // FedEx
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS]: ServiceLevel.PLUS,
  [FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS]: ServiceLevel.STANDARD,
  [FedexServices.INTERNATIONAL_FIRST]: ServiceLevel.PLUS,
  [FedexServices.FEDEX_GROUND]: ServiceLevel.STANDARD,
  [FedexServices.FEDEX_GROUND_HOME_DELIVERY]: ServiceLevel.STANDARD,
  [FedexServices.FEDEX_2_DAY]: ServiceLevel.PLUS,
  [FedexServices.PRIORITY_OVERNIGHT]: ServiceLevel.EXPRESS,
  [FedexServices.FEDEX_FIRST_FREIGHT]: ServiceLevel.EXPRESS, // or PLUS?
  [FedexServices.FEDEX_EXPRESS_SAVER]: ServiceLevel.PLUS,
  [FedexServices.FEDEX_3_DAY_FREIGHT]: ServiceLevel.STANDARD,
  [FedexServices.FEDEX_2_DAY_AM]: ServiceLevel.PLUS,
  [FedexServices.FEDEX_2_DAY_FREIGHT]: ServiceLevel.PLUS,
  [FedexServices.INTERNATIONAL_ECONOMY]: ServiceLevel.STANDARD,
  [FedexServices.FIRST_OVERNIGHT]: ServiceLevel.EXPRESS,
  [FedexServices.STANDARD_OVERNIGHT]: ServiceLevel.EXPRESS,
  [FedexServices.SMART_POST]: ServiceLevel.STANDARD,
  [FedexServices.FEDEX_1_DAY_FREIGHT]: ServiceLevel.EXPRESS,
  [FedexServices.INTERNATIONAL_PRIORITY]: ServiceLevel.PLUS,
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY]: ServiceLevel.PLUS,
  [FedexServices.EUROPE_FIRST_INTERNATIONAL_PRIORITY]: ServiceLevel.PLUS,
  [FedexServices.INTERNATIONAL_ECONOMY_FREIGHT]: ServiceLevel.STANDARD,
  [FedexServices.INTERNATIONAL_PRIORITY_FREIGHT]: ServiceLevel.PLUS,
};

const upsServiceToServiceLevel: Record<UpsServices, ServiceLevel> = {
  [UpsServices.Ground]: ServiceLevel.STANDARD,
  [UpsServices.NextDayAir]: ServiceLevel.EXPRESS,
  [UpsServices.ThreeDaySelect]: ServiceLevel.STANDARD,
  [UpsServices.SecondDayAirAM]: ServiceLevel.PLUS,
  [UpsServices.WorldWideSaver]: ServiceLevel.PLUS,
  [UpsServices.NextDayAirEarlyAM]: ServiceLevel.EXPRESS,
  [UpsServices.WorldWideExpressPlus]: ServiceLevel.EXPRESS,
  [UpsServices.WorldWideExpress]: ServiceLevel.PLUS,
  [UpsServices.NextDayAirSaver]: ServiceLevel.EXPRESS,
  [UpsServices.UPSStandard]: ServiceLevel.STANDARD,
  [UpsServices.SecondDayAir]: ServiceLevel.PLUS,
  [UpsServices.WorldWideExpedited]: ServiceLevel.STANDARD,
  [UpsServices.SurePostOver1Lb]: ServiceLevel.STANDARD,
  [UpsServices.SurePostUnder1Lb]: ServiceLevel.STANDARD,
  [UpsServices.ExpressEarly]: ServiceLevel.EXPRESS,
  [UpsServices.ExpressSaver]: ServiceLevel.EXPRESS,
};

const uspsServiceToServiceLevel: Record<UspsServices, ServiceLevel> = {
  [UspsServices.MediaMail]: ServiceLevel.STANDARD,
  [UspsServices.LibraryMail]: ServiceLevel.STANDARD,
  [UspsServices.GroundAdvantageReturn]: ServiceLevel.STANDARD,
  [UspsServices.FirstClassMailInternational]: ServiceLevel.STANDARD,
  [UspsServices.ExpressMailInternational]: ServiceLevel.PLUS,
  [UspsServices.PriorityMailInternational]: ServiceLevel.PLUS,
  [UspsServices.FirstClassPackageInternationalService]: ServiceLevel.STANDARD,
  [UspsServices.GroundAdvantage]: ServiceLevel.STANDARD,
  [UspsServices.FirstClass]: ServiceLevel.STANDARD,
  [UspsServices.PriorityMailReturn]: ServiceLevel.PLUS,
  [UspsServices.Priority]: ServiceLevel.PLUS,
  [UspsServices.Express]: ServiceLevel.PLUS,
  [UspsServices.PriorityMailExpressReturn]: ServiceLevel.EXPRESS,
};

const firstMileServiceToServiceLevel: Record<FirstMileServices, ServiceLevel> =
  {
    [FirstMileServices.XParcelGround]: ServiceLevel.STANDARD,
    [FirstMileServices.XParcelPriority]: ServiceLevel.PLUS,
    [FirstMileServices.XParcelExpedited]: ServiceLevel.EXPRESS,
    [FirstMileServices.XParcelExpeditedPlus]: ServiceLevel.EXPRESS,
  };

const australiaPostServiceToServiceLevel: Record<
  AustraliaPostServices,
  ServiceLevel
> = {
  [AustraliaPostServices.ParcelPost]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ExpressPost]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.International]: ServiceLevel.STANDARD,
  [AustraliaPostServices.Commercial]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ParcelPostSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ExpressPostSignature]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.InternationalSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.CommercialSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.EParcel1]: ServiceLevel.STANDARD,
  [AustraliaPostServices.EParcelPostReturns]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ExpressParcelPostReturns]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.ExpressPostEParcel]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.InternationalEconomyAirmailParcels]:
    ServiceLevel.STANDARD,
  [AustraliaPostServices.InternationalEconomyWSODREGPost]:
    ServiceLevel.STANDARD,
  [AustraliaPostServices.InternationalExpressDocSecIDocs]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.InternationalExpressMerchandise]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.InternationalExpressMerchECIMerch]:
    ServiceLevel.EXPRESS,
  [AustraliaPostServices.InternationalStandardPackTrack]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ParcelPostExtra]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ParcelPostWinePlusSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ParcelPostWineSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.ExpressEparcelPostReturns]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.EparcelPostReturns]: ServiceLevel.STANDARD,
  [AustraliaPostServices.IntlExpressDocs]: ServiceLevel.EXPRESS,
  [AustraliaPostServices.IntlStandardPackTrack]: ServiceLevel.STANDARD,
  [AustraliaPostServices.IntLStandardWithSignature]: ServiceLevel.STANDARD,
  [AustraliaPostServices.IntlExpressMerch]: ServiceLevel.EXPRESS,
};

const canadaPostServiceToServiceLevel: Record<
  CanadaPostServices,
  ServiceLevel
> = {
  [CanadaPostServices.RegularParcel]: ServiceLevel.STANDARD,
  [CanadaPostServices.ExpeditedParcel]: ServiceLevel.PLUS,
  [CanadaPostServices.Xpresspost]: ServiceLevel.EXPRESS,
  [CanadaPostServices.Priority]: ServiceLevel.EXPRESS,
  [CanadaPostServices.ExpeditedParcelUSA]: ServiceLevel.PLUS,
  [CanadaPostServices.SmallPacketUSAAir]: ServiceLevel.STANDARD,
  [CanadaPostServices.TrackedPacketUSA]: ServiceLevel.STANDARD,
  [CanadaPostServices.TrackedPacketUSALVM]: ServiceLevel.STANDARD,
  [CanadaPostServices.XpresspostUSA]: ServiceLevel.EXPRESS,
  [CanadaPostServices.XpresspostInternational]: ServiceLevel.EXPRESS,
  [CanadaPostServices.InternationalParcelAir]: ServiceLevel.PLUS,
  [CanadaPostServices.InternationalParcelSurface]: ServiceLevel.STANDARD,
  [CanadaPostServices.SmallPacketInternationalAir]: ServiceLevel.STANDARD,
  [CanadaPostServices.SmallPacketInternationalSurface]: ServiceLevel.STANDARD,
  [CanadaPostServices.TrackedPacketInternational]: ServiceLevel.STANDARD,
  [CanadaPostServices.ExpeditedParcelPlus]: ServiceLevel.PLUS,
};

const dhlExpressServiceToServiceLevel: Record<
  DhlExpressServices,
  ServiceLevel
> = {
  [DhlExpressServices.BreakBulkEconomy]: ServiceLevel.STANDARD,
  [DhlExpressServices.BreakBulkExpress]: ServiceLevel.PLUS,
  [DhlExpressServices.DomesticEconomySelect]: ServiceLevel.STANDARD,
  [DhlExpressServices.DomesticExpress]: ServiceLevel.PLUS,
  [DhlExpressServices.DomesticExpress1030]: ServiceLevel.PLUS,
  [DhlExpressServices.DomesticExpress1200]: ServiceLevel.PLUS,
  [DhlExpressServices.EconomySelect]: ServiceLevel.STANDARD,
  [DhlExpressServices.EconomySelectNonDoc]: ServiceLevel.STANDARD,
  [DhlExpressServices.EuroPack]: ServiceLevel.PLUS,
  [DhlExpressServices.EuropackNonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.Express1030]: ServiceLevel.PLUS,
  [DhlExpressServices.Express1030NonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.Express1200]: ServiceLevel.PLUS,
  [DhlExpressServices.Express1200NonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.Express900]: ServiceLevel.PLUS,
  [DhlExpressServices.Express900NonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressEasy]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressEasyNonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressEnvelope]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressWorldwide]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressWorldwideB2C]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressWorldwideB2CNonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressWorldwideECX]: ServiceLevel.PLUS,
  [DhlExpressServices.ExpressWorldwideNonDoc]: ServiceLevel.PLUS,
  [DhlExpressServices.FreightWorldwide]: ServiceLevel.STANDARD,
  [DhlExpressServices.GlobalmailBusiness]: ServiceLevel.STANDARD,
  [DhlExpressServices.JetLine]: ServiceLevel.PLUS,
  [DhlExpressServices.JumboBox]: ServiceLevel.STANDARD,
  [DhlExpressServices.LogisticsServices]: ServiceLevel.STANDARD,
  [DhlExpressServices.SameDay]: ServiceLevel.EXPRESS,
  [DhlExpressServices.SecureLine]: ServiceLevel.STANDARD,
  [DhlExpressServices.SprintLine]: ServiceLevel.PLUS,
  [DhlExpressServices.MedicalExpressNonDoc]: ServiceLevel.EXPRESS,
};

const dhlEcommServiceToServiceLevel: Record<DhlEcsServices, ServiceLevel> = {
  [DhlEcsServices.DhlParcelExpedited]: ServiceLevel.PLUS,
  [DhlEcsServices.DhlParcelExpeditedMax]: ServiceLevel.EXPRESS,
  [DhlEcsServices.DhlParcelGround]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlParcelInternationalDirect]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlParcelInternationalStandard]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlPacketInternational]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlParcelInternationalDirectPriority]: ServiceLevel.EXPRESS,
  [DhlEcsServices.DhlParcelInternationalDirectStandard]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlParcelInternationalDirectSMB]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlParcelInternationalStandardSMB]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlBPMExpedited]: ServiceLevel.EXPRESS,
  [DhlEcsServices.DhlBPMGround]: ServiceLevel.EXPRESS,
  [DhlEcsServices.DhlSmartMailParcelReturnGround]: ServiceLevel.STANDARD,
  [DhlEcsServices.DhlSmartMailParcelReturnPlus]: ServiceLevel.PLUS,
  [DhlEcsServices.DhlSmartMailParcelReturnLight]: ServiceLevel.STANDARD,
};

//
const apcServiceToServiceLevel: Record<ApcServices, ServiceLevel> = {
  [ApcServices.ParcelConnectBookService]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectExpeditedDDP]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectExpeditedDDU]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPriorityDDP]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPriorityDDPDelcon]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPriorityDDU]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPriorityDDUDelcon]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPriorityDDUPQW]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectStandardDDU]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectStandardDDUPQW]: ServiceLevel.STANDARD,
  [ApcServices.ParcelConnectPacketDDU]: ServiceLevel.STANDARD,
};

const glsServiceToServiceLevel: Record<GlsServices, ServiceLevel> = {
  [GlsServices.EarlyPriorityOvernight]: ServiceLevel.STANDARD,
  [GlsServices.PriorityOvernight]: ServiceLevel.STANDARD,
  [GlsServices.CaliforniaParcelService]: ServiceLevel.STANDARD,
  [GlsServices.SaturdayDeliveryService]: ServiceLevel.STANDARD,
  [GlsServices.EarlySaturdayService]: ServiceLevel.STANDARD,
  [GlsServices.NoonPriorityService]: ServiceLevel.STANDARD,
};
const royalMailV3ServiceToServiceLevel: Record<
  RoyalMailV3Services,
  ServiceLevel
> = {
  [RoyalMailV3Services.RoyalMailV3]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportRoyalMail24PlusParcelsWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportStandard24LargeLetter]: ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportStandard24LargeLetterWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportStandard24Parcel]: ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportStandard24ParcelWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportStandard48LargeLetter]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportStandard48LargeLetterWeekendService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableHighVolumeWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportTracked24ParcelHighVolumeWeekendService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableHighVolumeWeekendService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableWeekendService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportTracked48ParcelHighVolumeWeekendService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeAGE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUMaxSortLargeLetter]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUMaxSortParcel]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUSignedParcel]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUTrackedHighVolLargeLetter]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUTrackedParcel]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUTrackedSignedLargeLetter]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.DEImporttoEUTrackedSignedParcel]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.HMForcesMail]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.HMForcesSignedFor]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.HMForcesSpecialDelivery1000]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.HMForcesSpecialDelivery2500]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.HMForcesSpecialDelivery500]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.ImportDETracked24]: ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked24HighVolume]: ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked24Letterboxable]: ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked24LetterboxableHighVolume]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked24SignatureHighVolumeAGE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked24SignatureStandardAGE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETracked48]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.ImportDETracked48HighVolume]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.ImportDETracked48Letterboxable]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.ImportDETracked48LetterboxableHighVolume]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.ImportDETrackedReturns24]: ServiceLevel.PLUS,
  [RoyalMailV3Services.ImportDETrackedReturns48]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriorityETOE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountrySortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortPriorityService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortResidueStandard]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortStandardService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterSemiTracked]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriorityMachine]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriorityMachine]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLetterMaxSortResidueStandard]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLettersMaxSortStandard]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailLettersZeroSortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailLettersZoneSortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessMailSignedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailSignedExtraCompensationCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailSignedHighVolCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailSignedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolExtraCompCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedLargeLetterCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessMailTrackedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxable]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxableDDP]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxable]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableDDP]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcels]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectStandard]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedDirectIrelandCountry]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedDDP]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedZoneSort]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriority]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriorityETOE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessParcelsZoneSortPriorityService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortLetter]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessPrintedMatterPacket]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalBusinessTrackedExpressNPC]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.InternationalEconomyOnAccount]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalSignedOnAccount]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalSignedOnAccountExtraComp]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalStandardOnAccount]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedOnAccount]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedOnAccountExtraComp]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kg]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgCPrio]: ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEC]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraComp]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEC]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEE]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedParcels30kgXCompCPrio]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.InternationalTrackedSignedOnAccount]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.InternationalTrackedSignedOnAccountExtraComp]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.ParcelpostFlatRateAnnual]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RM24LargeLetterAnnualFlatRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RM24PresortedLargeLetterAnnualFlatRate]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RM24PresortedParcelsAnnualFlatRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RM48LargeLetterAnnualFlatRate]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RM48PresortedLargeLetterAnnualFlatRate]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RM48PresortedParcelsAnnualFlatRate]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail1stClass]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail1stClassLettersDailyRateservice]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail1stClassSignedFor]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24LargeLetterDailyRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRateService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24PacketpostFlatRateService]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24ParcelDailyRateService]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24ParcelSort8DailyRateService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24ParcelSort8FlatRateService]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24ParcelsDailyRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24ParcelsFlatRate]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24PresortedLargeLetter]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24PresortedParcels]: ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24SORT8LargeLetterAnnualFlatRate]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail24SORT8ParcelsAnnualFlatRate]:
    ServiceLevel.PLUS,
  [RoyalMailV3Services.RoyalMail2ndClass]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail2ndClassLettersDailyRateservice]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail2ndClassSignedFor]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48LargeLetterDailyRate]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRate]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRateService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48PacketpostFlatRateService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48ParcelDailyRateService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48ParcelSort8DailyRateService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48ParcelSort8FlatRateService]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48ParcelsDailyRate]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48ParcelsFlatRate]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48PresortedLargeLetter]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48PresortedParcels]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48SORT8LargeLetterAnnualFlatRate]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.RoyalMail48Sort8ParcelsAnnualFlatRate]:
    ServiceLevel.STANDARD,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed1000]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed2500]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed750]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE1000]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE2500]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE750]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM750]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE750]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID750]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM750]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE750]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID1000]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID2500]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID750]:
    ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID1000]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID2500]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID750]: ServiceLevel.EXPRESS,
  [RoyalMailV3Services.Tracked24]: ServiceLevel.PLUS,
  [RoyalMailV3Services.Tracked24HighVolume]: ServiceLevel.PLUS,
  [RoyalMailV3Services.Tracked24HighVolumeSignatureAGE]: ServiceLevel.PLUS,
  [RoyalMailV3Services.Tracked24SignatureAGE]: ServiceLevel.PLUS,
  [RoyalMailV3Services.Tracked48]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.Tracked48HighVolume]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.Tracked48HighVolumeSignatureAGE]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.Tracked48SignatureAGE]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.TrackedLetterBoxable24]: ServiceLevel.PLUS,
  [RoyalMailV3Services.TrackedLetterBoxable24HighVolume]: ServiceLevel.PLUS,
  [RoyalMailV3Services.TrackedLetterBoxable48]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.TrackedLetterBoxable48HighVolume]: ServiceLevel.STANDARD,
  [RoyalMailV3Services.TrackedReturns24]: ServiceLevel.PLUS,
  [RoyalMailV3Services.TrackedReturns48]: ServiceLevel.STANDARD,
};

const asendiaServiceToServiceLevel: Record<AsendiaServices, ServiceLevel> = {
  [AsendiaServices.ADS]: ServiceLevel.STANDARD,
  [AsendiaServices.AirFreightInbound]: ServiceLevel.STANDARD,
  [AsendiaServices.AirFreightOutbound]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterExpedited]:
    ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterGround]:
    ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticFlatsExpedited]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticFlatsGround]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelGroundOver1lb]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelGroundUnder1lb]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelMAXOver1lb]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelMAXUnder1lb]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelOver1lbExpedited]:
    ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelUnder1lbExpedited]:
    ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticPromoParcelExpedited]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticPromoParcelGround]: ServiceLevel.STANDARD,
  [AsendiaServices.BulkFreight]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailCanadaLettermail]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailCanadaLettermailMachineable]:
    ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailEconomyLPWholesale]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailEconomySPWholesale]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailIPA]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailISAL]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailPriority]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailPriorityLPWholesale]: ServiceLevel.STANDARD,
  [AsendiaServices.BusinessMailPrioritySPWholesale]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailCanadaPersonalizedLCP]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailCanadaPersonalizedMachineable]:
    ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailCanadaPersonalizedNDG]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailIPA]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailISAL]: ServiceLevel.STANDARD,
  [AsendiaServices.MarketingMailPriority]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsCanadaLCP]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsCanadaNDG]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsIPA]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsISAL]: ServiceLevel.STANDARD,
  [AsendiaServices.PublicationsPriority]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQElite]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteCustom]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteDAP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteDDPOversized]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteDPD]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteDirectAccessCanadaDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQEliteOversized]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlus]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusCustom]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusCustomsPrepaid]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusDAP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusWholesale]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusePacket]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusePacketCanadaCustomsPrePaid]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQPlusePacketCanadaDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQReturnsDomestic]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQReturnsInternational]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelect]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectCustom]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectCustomsPrepaidByShopper]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDAP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDDPDirectAccess]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDirectAccess]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDirectAccessCanadaDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectOversized]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectOversizedDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMEI]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMEICanadaCustomsPrePaid]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMEIPCPostage]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMI]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMICanadaCustomsPrepaid]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMICanadaDDP]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMINonPresort]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectPMIPCPostage]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQStandard]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQStandardCustom]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQStandardEconomy]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQStandardIPA]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQStandardISAL]: ServiceLevel.STANDARD,
  [AsendiaServices.ePaqSelectPMEINonPresort]: ServiceLevel.STANDARD,
  [AsendiaServices.ePAQSelectDDPDG]: ServiceLevel.STANDARD,
  [AsendiaServices.AsendiaDomesticParcelMaxUnder70lb]: ServiceLevel.STANDARD,
};

const passportGlobalServiceToServiceLevel: Record<
  PassportGlobalServices,
  ServiceLevel
> = {
  [PassportGlobalServices.PriorityDdpDelcon]: ServiceLevel.STANDARD,
  [PassportGlobalServices.PriorityDdp]: ServiceLevel.STANDARD,
  [PassportGlobalServices.epacketDdp]: ServiceLevel.STANDARD,
  [PassportGlobalServices.ExpressDdp]: ServiceLevel.EXPRESS,
  [PassportGlobalServices.PriorityDduDelcon]: ServiceLevel.STANDARD,
  [PassportGlobalServices.PriorityDdu]: ServiceLevel.STANDARD,
  [PassportGlobalServices.ExpeditedDdpDelcon]: ServiceLevel.STANDARD,
  [PassportGlobalServices.ExpressDdpDelcon]: ServiceLevel.EXPRESS,
  [PassportGlobalServices.GlobalPlus]: ServiceLevel.PLUS,
};

const speeDeeServiceToServiceLevel: Record<SpeeDeeServices, ServiceLevel> = {
  [SpeeDeeServices.SpeeDeeDelivery]: ServiceLevel.STANDARD,
};

const landmarkGlobalServiceToServiceLevel: Record<
  LandmarkGlobalServices,
  ServiceLevel
> = { [LandmarkGlobalServices.Delivery]: ServiceLevel.STANDARD };

const etaExpressServiceToServiceLevel: Record<
  ETAExpressServices,
  ServiceLevel
> = { [ETAExpressServices.ETAExpress]: ServiceLevel.STANDARD };

export function carrierAndServiceToServiceLevel(
  carrier: Carriers,
  service:
    | FedexServices
    | UpsServices
    | UspsServices
    | AmazonServices
    | DhlExpressServices
    | DhlEcsServices,
) {
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST: {
      if (isFedexService(service)) {
        return fedexServiceToServiceLevel[service];
      }
      break;
    }
    case Carriers.UPS_SUREPOST:
    case Carriers.UPS: {
      if (isUpsService(service)) {
        return upsServiceToServiceLevel[service];
      }
      break;
    }
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS: {
      if (isUspsService(service)) {
        return uspsServiceToServiceLevel[service];
      }
      break;
    }
    case Carriers.AMAZON:
      if (isAmazonService(service)) {
        return ServiceLevel.STANDARD;
      }
      break;
    case Carriers.DHL_EXPRESS:
      if (isDhlExpressService(service)) {
        return dhlExpressServiceToServiceLevel[service];
      }
      break;
    case Carriers.VEHO:
      if (isVehoService(service)) {
        return ServiceLevel.EXPRESS;
      }
      break;
    case Carriers.FIRST_MILE:
      if (isFirstMileService(service)) {
        return firstMileServiceToServiceLevel[service];
      }
      break;
    case Carriers.AUSTRALIA_POST:
      if (isAustraliaPostService(service)) {
        return australiaPostServiceToServiceLevel[service];
      }
      break;
    case Carriers.CANADA_POST:
      if (isCanadaPostService(service)) {
        return canadaPostServiceToServiceLevel[service];
      }
      break;
    case Carriers.DHL_ECS:
      if (isDhlEcsService(service)) {
        return dhlEcommServiceToServiceLevel[service];
      }
      break;
    case Carriers.APC:
      if (isApcService(service)) {
        return apcServiceToServiceLevel[service];
      }
      break;
    case Carriers.GLS:
      if (isGlsService(service)) {
        return glsServiceToServiceLevel[service];
      }
      break;
    case Carriers.ASENDIA:
      if (isAsendiaService(service)) {
        return asendiaServiceToServiceLevel[service];
      }
      break;
    case Carriers.ROYAL_MAIL_V3:
      if (isRoyalMailV3Service(service)) {
        return royalMailV3ServiceToServiceLevel[service];
      }
      break;
    case Carriers.SPEE_DEE:
      if (isSpeeDeeService(service)) {
        return speeDeeServiceToServiceLevel[service];
      }
      break;
    case Carriers.PASSPORT_GLOBAL:
      if (isPassportGlobalService(service)) {
        return passportGlobalServiceToServiceLevel[service];
      }
      break;
    case Carriers.EPOST_GLOBAL:
      return ServiceLevel.STANDARD;
    case Carriers.LANDMARK_GLOBAL:
      if (isLandmarkGlobalService(service)) {
        return landmarkGlobalServiceToServiceLevel[service];
      }
      break;
    case Carriers.ETA_EXPRESS:
      if (isETAExpressService(service)) {
        return etaExpressServiceToServiceLevel[service];
      }
      break;
    case Carriers.CROSSBORDERLY:
      break;
    default:
      assertNever(carrier);
  }

  throw new Error(
    `Unknown carrier-service combination: ${carrier} and ${service}`,
  );
}

export function getAllServicesArray() {
  return [
    ...Object.values(FedexServices),
    ...Object.values(UpsServices),
    ...Object.values(UspsServices),
    ...Object.values(AmazonServices),
    ...Object.values(DhlExpressServices),
    ...Object.values(VehoServices),
    ...Object.values(ApcServices),
    ...Object.values(FirstMileServices),
    ...Object.values(AustraliaPostServices),
    ...Object.values(CanadaPostServices),
    ...Object.values(DhlEcsServices),
    ...Object.values(GlsServices),
    ...Object.values(AsendiaServices),
    ...Object.values(RoyalMailV3Services),
    ...Object.values(SpeeDeeServices),
    ...Object.values(PassportGlobalServices),
    ...Object.values(EPostGlobalServices),
    ...Object.values(LandmarkGlobalServices),
    ...Object.values(ETAExpressServices),
    ...Object.values(CrossborderlyServices),
  ];
}

export const ServiceSchema = z.union([
  createEnum(FedexServices, { enumName: "FedexServices" }),
  createEnum(UpsServices, { enumName: "UpsServices" }),
  createEnum(UspsServices, { enumName: "UspsServices" }),
  createEnum(AmazonServices, { enumName: "AmazonServices" }),
  createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  createEnum(VehoServices, { enumName: "VehoServices" }),
  createEnum(ApcServices, { enumName: "ApcServices" }),
  createEnum(FirstMileServices, { enumName: "FirstMileServices" }),
  createEnum(AustraliaPostServices, { enumName: "AustraliaPostServices" }),
  createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }),
  createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  createEnum(GlsServices, { enumName: "GlsServices" }),
  createEnum(AsendiaServices, { enumName: "AsendiaServices" }),
  createEnum(RoyalMailV3Services, { enumName: "RoyalMailV3Services" }),
  createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }),
  createEnum(PassportGlobalServices, { enumName: "PassportGlobalServices" }),
  createEnum(EPostGlobalServices, { enumName: "EPostGlobalServices" }),
  createEnum(LandmarkGlobalServices, { enumName: "LandmarkGlobalServices" }),
  createEnum(ETAExpressServices, { enumName: "ETAExpressServices" }),
  createEnum(CrossborderlyServices, { enumName: "CrossborderlyServices" }),
]);
export type Service = z.infer<typeof ServiceSchema>;

export const CarrierSchema = createEnum(Carriers, { enumName: "Carriers" });

export type Carrier = z.infer<typeof CarrierSchema>;

export const CarrierAndServiceSchema = z.object({
  carrier: CarrierSchema,
  service: ServiceSchema,
});
export type CarrierAndService = z.infer<typeof CarrierAndServiceSchema>;

export const SelectedShippingRateSchema = CarrierAndServiceSchema.extend({
  carrier_account_id: z.string().nullish(),
});
export type SelectedShippingRate = z.infer<typeof SelectedShippingRateSchema>;
export interface ISelectedShippingRate
  extends z.ZodType<SelectedShippingRate> {}

export const CorrelatedCarrierServiceSchema = z.union([
  z.object({
    carrier: createEnum([Carriers.FEDEX, Carriers.FEDEX_SMART_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(FedexServices, { enumName: "FedexServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.UPS, Carriers.UPS_SUREPOST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(UpsServices, { enumName: "UpsServices" }),
  }),
  z.object({
    carrier: createEnum(
      [Carriers.USPS, Carriers.USPS_RETURNS, Carriers.USPS_SHIP],
      { enumName: "Carriers", isPartial: true, originalEnum: Carriers },
    ),
    service: createEnum(UspsServices, { enumName: "UspsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.AMAZON], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AmazonServices, { enumName: "AmazonServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.DHL_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.VEHO], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(VehoServices, { enumName: "VehoServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.APC], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(ApcServices, { enumName: "ApcServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.FIRST_MILE], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(FirstMileServices, { enumName: "FirstMileServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.DHL_ECS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.AUSTRALIA_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AustraliaPostServices, {
      enumName: "AustraliaPostServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.CANADA_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.GLS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(GlsServices, { enumName: "GlsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.ASENDIA], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AsendiaServices, { enumName: "AsendiaServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.ROYAL_MAIL_V3], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(RoyalMailV3Services, {
      enumName: "RoyalMailV3Services",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.SPEE_DEE], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.PASSPORT_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(PassportGlobalServices, {
      enumName: "PassportGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.EPOST_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(EPostGlobalServices, {
      enumName: "EPostGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.LANDMARK_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(LandmarkGlobalServices, {
      enumName: "LandmarkGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.ETA_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(ETAExpressServices, { enumName: "ETAExpressServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.CROSSBORDERLY], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(CrossborderlyServices, {
      enumName: "CrossborderlyServices",
    }),
  }),
]);
export type CorrelatedCarrierService = z.infer<
  typeof CorrelatedCarrierServiceSchema
>;

// Type check to ensure all Carriers are represented in CorrelatedCarrierServiceSchema
// export to consider variable "used"
export const _carriersAreExhaustive: Exclude<
  Carriers,
  CorrelatedCarrierService["carrier"]
> extends never
  ? true
  : never = true;

export function getAllCarrierServiceCombinations(): CorrelatedCarrierService[] {
  const combinations: CorrelatedCarrierService[] = [];

  const carriers = Object.values(Carriers);
  for (const carrier of carriers) {
    switch (carrier) {
      case Carriers.FEDEX:
        for (const service of Object.values(FedexServices)) {
          // These go under the FEDEX_SMART_POST carrier
          if (service === FedexServices.SMART_POST) {
            continue;
          }
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.UPS:
        for (const service of Object.values(UpsServices)) {
          // These go under the UPS_SUREPOST carrier
          if (service === UpsServices.SurePostUnder1Lb) {
            continue;
          }
          if (service === UpsServices.SurePostOver1Lb) {
            continue;
          }
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.USPS:
        for (const service of Object.values(UspsServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.AMAZON:
        for (const service of Object.values(AmazonServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.DHL_EXPRESS:
        for (const service of Object.values(DhlExpressServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.DHL_ECS:
        for (const service of Object.values(DhlEcsServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.VEHO:
        for (const service of Object.values(VehoServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.APC:
        for (const service of Object.values(ApcServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.FIRST_MILE:
        for (const service of Object.values(FirstMileServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.AUSTRALIA_POST:
        for (const service of Object.values(AustraliaPostServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.CANADA_POST:
        for (const service of Object.values(CanadaPostServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.GLS:
        for (const service of Object.values(GlsServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.ASENDIA:
        for (const service of Object.values(AsendiaServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.ROYAL_MAIL_V3:
        for (const service of Object.values(RoyalMailV3Services)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.SPEE_DEE:
        for (const service of Object.values(SpeeDeeServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.PASSPORT_GLOBAL:
        for (const service of Object.values(PassportGlobalServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.EPOST_GLOBAL:
        for (const service of Object.values(EPostGlobalServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.LANDMARK_GLOBAL:
        for (const service of Object.values(LandmarkGlobalServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.ETA_EXPRESS:
        for (const service of Object.values(ETAExpressServices)) {
          combinations.push({ carrier, service });
        }
        break;
      case Carriers.UPS_SUREPOST:
        combinations.push({ carrier, service: UpsServices.SurePostUnder1Lb });
        combinations.push({ carrier, service: UpsServices.SurePostOver1Lb });
        break;
      case Carriers.FEDEX_SMART_POST:
        combinations.push({ carrier, service: FedexServices.SMART_POST });
        break;
      case Carriers.USPS_RETURNS:
      case Carriers.USPS_SHIP:
      case Carriers.CROSSBORDERLY:
        break;
      default:
        assertNever(carrier);
    }
  }
  return combinations;
}

export const fedexServiceToLabel: Record<FedexServices, string> = {
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS]:
    "FedEx International Priority® Express",
  [FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS]:
    "FedEx International Connect Plus",
  [FedexServices.INTERNATIONAL_FIRST]: "FedEx International First®",
  [FedexServices.FEDEX_GROUND]: "FedEx Ground®",
  [FedexServices.FEDEX_GROUND_HOME_DELIVERY]: "FedEx Ground Home Delivery®",
  [FedexServices.FEDEX_2_DAY]: "FedEx 2Day®",
  [FedexServices.PRIORITY_OVERNIGHT]: "FedEx Priority Overnight®",
  [FedexServices.FEDEX_FIRST_FREIGHT]: "FedEx First Freight",
  [FedexServices.FEDEX_EXPRESS_SAVER]: "FedEx Express Saver®",
  [FedexServices.FEDEX_3_DAY_FREIGHT]: "FedEx 3Day® Freight",
  [FedexServices.FEDEX_2_DAY_AM]: "FedEx 2Day® AM",
  [FedexServices.FEDEX_2_DAY_FREIGHT]: "FedEx 2Day® Freight",
  [FedexServices.INTERNATIONAL_ECONOMY]: "FedEx International Economy®",
  [FedexServices.FIRST_OVERNIGHT]: "FedEx First Overnight®",
  [FedexServices.STANDARD_OVERNIGHT]: "FedEx Standard Overnight®",
  [FedexServices.SMART_POST]: "FedEx Ground® Economy",
  [FedexServices.FEDEX_1_DAY_FREIGHT]: "FedEx 1Day® Freight",
  [FedexServices.INTERNATIONAL_PRIORITY]: "FedEx International Priority®",
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY]:
    "FedEx International Priority®",
  [FedexServices.EUROPE_FIRST_INTERNATIONAL_PRIORITY]:
    "FedEx Europe First International Priority®",
  [FedexServices.INTERNATIONAL_ECONOMY_FREIGHT]:
    "FedEx International Economy® Freight",
  [FedexServices.INTERNATIONAL_PRIORITY_FREIGHT]:
    "FedEx International Priority® Freight",
};

// https://ups.tescom-usa.com/Content/docs/trademarks.pdf
export const upsServiceToLabel: Record<UpsServices, string> = {
  [UpsServices.Ground]: "Ground",
  [UpsServices.NextDayAir]: "Next Day Air®",
  [UpsServices.ThreeDaySelect]: "3 Day Select®",
  [UpsServices.SecondDayAirAM]: "2nd Air A.M.®",
  [UpsServices.WorldWideSaver]: "Worldwide Saver®",
  [UpsServices.NextDayAirEarlyAM]: "Next Day Air® Early",
  [UpsServices.WorldWideExpressPlus]: "Worldwide Express Plus®",
  [UpsServices.WorldWideExpress]: "Worldwide Express®",
  [UpsServices.NextDayAirSaver]: "Next Day Air Saver®",
  [UpsServices.UPSStandard]: "Standard",
  [UpsServices.SecondDayAir]: "2nd Day Air®",
  [UpsServices.WorldWideExpedited]: "Worldwide Expedited®",
  [UpsServices.SurePostUnder1Lb]: "Ground Saver (under 1lb)",
  [UpsServices.SurePostOver1Lb]: "Ground Saver (over 1lb)",
  [UpsServices.ExpressEarly]: "Express Early®",
  [UpsServices.ExpressSaver]: "Express Saver®",
};

export const uspsServiceToLabel: Record<UspsServices, string> = {
  [UspsServices.GroundAdvantageReturn]: "USPS Ground Advantage Return",
  [UspsServices.ExpressMailInternational]: "USPS Express Mail International",
  [UspsServices.PriorityMailInternational]: "USPS Priority Mail International",
  [UspsServices.FirstClassMailInternational]:
    "USPS First Class Mail International",
  [UspsServices.FirstClassPackageInternationalService]:
    "USPS First Class Package International Service",
  [UspsServices.GroundAdvantage]: "USPS Ground Advantage",
  [UspsServices.PriorityMailReturn]: "USPS Priority Mail Return",
  [UspsServices.Priority]: "USPS Priority",
  [UspsServices.Express]: "USPS Express",
  [UspsServices.FirstClass]: "USPS First Class",
  [UspsServices.PriorityMailExpressReturn]: "USPS Priority Mail Express Return",
  [UspsServices.MediaMail]: "USPS Media Mail",
  [UspsServices.LibraryMail]: "USPS Library Mail",
};

export const amazonServiceToLabel: Record<AmazonServices, string> = {
  [AmazonServices.Ground]: "Amazon Shipping Ground",
};

export const vehoServiceToLabel: Record<VehoServices, string> = {
  [VehoServices.NextDay]: "Veho Next Day",
  [VehoServices.SameDay]: "Veho Same Day",
};

export const apcServiceToLabel: Record<ApcServices, string> = {
  [ApcServices.ParcelConnectBookService]: "APC Parcel Connect Book Service",
  [ApcServices.ParcelConnectExpeditedDDP]: "APC Parcel Connect Expedited DDP",
  [ApcServices.ParcelConnectExpeditedDDU]: "APC Parcel Connect Expedited DDU",
  [ApcServices.ParcelConnectPriorityDDP]: "APC Parcel Connect Priority DDP",
  [ApcServices.ParcelConnectPriorityDDPDelcon]:
    "APC Parcel Connect Priority DDP Delcon",
  [ApcServices.ParcelConnectPriorityDDU]: "APC Parcel Connect Priority DDU",
  [ApcServices.ParcelConnectPriorityDDUDelcon]:
    "APC Parcel Connect Priority DDU Delcon",
  [ApcServices.ParcelConnectPriorityDDUPQW]:
    "APC Parcel Connect Priority DDU PQW",
  [ApcServices.ParcelConnectStandardDDU]: "APC Parcel Connect Standard DDU",
  [ApcServices.ParcelConnectStandardDDUPQW]:
    "APC Parcel Connect Standard DDU PQW",
  [ApcServices.ParcelConnectPacketDDU]: "APC Parcel Connect Packet DDU",
};

export const firstMileServiceToLabel: Record<FirstMileServices, string> = {
  [FirstMileServices.XParcelGround]: "FirstMile XParcel Ground",
  [FirstMileServices.XParcelPriority]: "FirstMile XParcel Priority",
  [FirstMileServices.XParcelExpedited]: "FirstMile XParcel Expedited",
  [FirstMileServices.XParcelExpeditedPlus]: "FirstMile XParcel Expedited Plus",
};

export const dhlExpressServiceToLabel: Record<DhlExpressServices, string> = {
  [DhlExpressServices.BreakBulkEconomy]: "DHL Express Break Bulk Economy",
  [DhlExpressServices.BreakBulkExpress]: "DHL Express Break Bulk Express",
  [DhlExpressServices.DomesticEconomySelect]:
    "DHL Express Domestic Economy Select",
  [DhlExpressServices.DomesticExpress]: "DHL Express Domestic Express",
  [DhlExpressServices.DomesticExpress1030]:
    "DHL Express Domestic Express 10:30",
  [DhlExpressServices.DomesticExpress1200]:
    "DHL Express Domestic Express 12:00",
  [DhlExpressServices.EconomySelect]: "DHL Express Economy Select",
  [DhlExpressServices.EconomySelectNonDoc]:
    "DHL Express Economy Select Non-Doc",
  [DhlExpressServices.EuroPack]: "DHL Express EuroPack",
  [DhlExpressServices.EuropackNonDoc]: "DHL Express Europack Non-Doc",
  [DhlExpressServices.Express1030]: "DHL Express Express 10:30",
  [DhlExpressServices.Express1030NonDoc]: "DHL Express Express 10:30 Non-Doc",
  [DhlExpressServices.Express1200]: "DHL Express Express 12:00",
  [DhlExpressServices.Express1200NonDoc]: "DHL Express Express 12:00 Non-Doc",
  [DhlExpressServices.Express900]: "DHL Express Express 9:00",
  [DhlExpressServices.Express900NonDoc]: "DHL Express Express 9:00 Non-Doc",
  [DhlExpressServices.ExpressEasy]: "DHL Express Express Easy",
  [DhlExpressServices.ExpressEasyNonDoc]: "DHL Express Express Easy Non-Doc",
  [DhlExpressServices.ExpressEnvelope]: "DHL Express Express Envelope",
  [DhlExpressServices.ExpressWorldwide]: "DHL Express Express Worldwide",
  [DhlExpressServices.ExpressWorldwideB2C]: "DHL Express Express Worldwide B2C",
  [DhlExpressServices.ExpressWorldwideB2CNonDoc]:
    "DHL Express Express Worldwide B2C Non-Doc",
  [DhlExpressServices.ExpressWorldwideECX]: "DHL Express Express Worldwide ECX",
  [DhlExpressServices.ExpressWorldwideNonDoc]:
    "DHL Express Express Worldwide Non-Doc",
  [DhlExpressServices.FreightWorldwide]: "DHL Express Freight Worldwide",
  [DhlExpressServices.GlobalmailBusiness]: "DHL Express GlobalMail Business",
  [DhlExpressServices.JetLine]: "DHL Express JetLine",
  [DhlExpressServices.JumboBox]: "DHL Express Jumbo Box",
  [DhlExpressServices.LogisticsServices]: "DHL Express Logistics Services",
  [DhlExpressServices.SameDay]: "DHL Express Same Day",
  [DhlExpressServices.SecureLine]: "DHL Express SecureLine",
  [DhlExpressServices.SprintLine]: "DHL Express SprintLine",
  [DhlExpressServices.MedicalExpressNonDoc]:
    "DHL Express Medical Express Non-Doc",
};

export const dhlEcsServiceToLabel: Record<DhlEcsServices, string> = {
  [DhlEcsServices.DhlParcelExpedited]: "DHL Parcel Expedited",
  [DhlEcsServices.DhlParcelExpeditedMax]: "DHL Parcel Expedited Max",
  [DhlEcsServices.DhlParcelGround]: "DHL Parcel Ground",
  [DhlEcsServices.DhlParcelInternationalDirect]:
    "DHL Parcel International Direct",
  [DhlEcsServices.DhlParcelInternationalStandard]:
    "DHL Parcel International Standard",
  [DhlEcsServices.DhlPacketInternational]: "DHL Packet International",
  [DhlEcsServices.DhlParcelInternationalDirectPriority]:
    "DHL Parcel International Direct Priority",
  [DhlEcsServices.DhlParcelInternationalDirectStandard]:
    "DHL Parcel International Direct Standard",
  [DhlEcsServices.DhlParcelInternationalDirectSMB]:
    "DHL Parcel International Direct SMB",
  [DhlEcsServices.DhlParcelInternationalStandardSMB]:
    "DHL Parcel International Standard SMB",
  [DhlEcsServices.DhlBPMExpedited]: "DHL Parcel BPM Expedited",
  [DhlEcsServices.DhlBPMGround]: "DHL Parcel BPM Ground",
  [DhlEcsServices.DhlSmartMailParcelReturnGround]:
    "DHL Parcel Smart Mail Parcel Return Ground",
  [DhlEcsServices.DhlSmartMailParcelReturnPlus]:
    "DHL Parcel Smart Mail Parcel Return Plus",
  [DhlEcsServices.DhlSmartMailParcelReturnLight]:
    "DHL Parcel Smart Mail Parcel Return Light",
};

export const australiaPostServiceToLabel: Record<
  AustraliaPostServices,
  string
> = {
  [AustraliaPostServices.ParcelPost]: "Australia Post Parcel Post",
  [AustraliaPostServices.ExpressPost]: "Australia Post Express Post",
  [AustraliaPostServices.International]: "Australia Post International",
  [AustraliaPostServices.Commercial]: "Australia Post Commercial",
  [AustraliaPostServices.ParcelPostSignature]:
    "Australia Post Parcel Post Signature",
  [AustraliaPostServices.ExpressPostSignature]:
    "Australia Post Express Post Signature",
  [AustraliaPostServices.InternationalSignature]:
    "Australia Post International Signature",
  [AustraliaPostServices.CommercialSignature]:
    "Australia Post Commercial Signature",
  [AustraliaPostServices.EParcel1]: "Australia Post E-Parcel",
  [AustraliaPostServices.EParcelPostReturns]: "Australia Post E-Parcel Returns",
  [AustraliaPostServices.ExpressParcelPostReturns]:
    "Australia Post Express Parcel Returns",
  [AustraliaPostServices.ExpressPostEParcel]: "Australia Post Express E-Parcel",
  [AustraliaPostServices.InternationalEconomyAirmailParcels]:
    "Australia Post International Economy Airmail Parcels",
  [AustraliaPostServices.InternationalEconomyWSODREGPost]:
    "Australia Post International Economy WSODREG",
  [AustraliaPostServices.InternationalExpressDocSecIDocs]:
    "Australia Post International Express Documents",
  [AustraliaPostServices.InternationalExpressMerchandise]:
    "Australia Post International Express Merchandise",
  [AustraliaPostServices.InternationalExpressMerchECIMerch]:
    "Australia Post International Express Merchandise ECI",
  [AustraliaPostServices.InternationalStandardPackTrack]:
    "Australia Post International Standard Pack & Track",
  [AustraliaPostServices.ParcelPostExtra]: "Australia Post Parcel Post Extra",
  [AustraliaPostServices.ParcelPostWinePlusSignature]:
    "Australia Post Parcel Post Wine Plus Signature",
  [AustraliaPostServices.ParcelPostWineSignature]:
    "Australia Post Parcel Post Wine Signature",
  [AustraliaPostServices.ExpressEparcelPostReturns]:
    "Australia Post Express E-Parcel Returns",
  [AustraliaPostServices.EparcelPostReturns]: "Australia Post E-Parcel Returns",
  [AustraliaPostServices.IntlExpressDocs]:
    "Australia Post Intl Express Documents",
  [AustraliaPostServices.IntlStandardPackTrack]:
    "Australia Post Intl Standard Pack & Track",
  [AustraliaPostServices.IntLStandardWithSignature]:
    "Australia Post Intl Standard with Signature",
  [AustraliaPostServices.IntlExpressMerch]:
    "Australia Post Intl Express Merchandise",
};

export const canadaPostServiceToLabel: Record<CanadaPostServices, string> = {
  [CanadaPostServices.RegularParcel]: "Canada Post Regular Parcel",
  [CanadaPostServices.ExpeditedParcel]: "Canada Post Expedited Parcel",
  [CanadaPostServices.Xpresspost]: "Canada Post Xpresspost",
  [CanadaPostServices.Priority]: "Canada Post Priority",
  [CanadaPostServices.ExpeditedParcelUSA]: "Canada Post Expedited Parcel USA",
  [CanadaPostServices.SmallPacketUSAAir]: "Canada Post Small Packet USA Air",
  [CanadaPostServices.TrackedPacketUSA]: "Canada Post Tracked Packet USA",
  [CanadaPostServices.TrackedPacketUSALVM]:
    "Canada Post Tracked Packet USA LVM",
  [CanadaPostServices.XpresspostUSA]: "Canada Post Xpresspost USA",
  [CanadaPostServices.XpresspostInternational]:
    "Canada Post Xpresspost International",
  [CanadaPostServices.InternationalParcelAir]:
    "Canada Post International Parcel Air",
  [CanadaPostServices.InternationalParcelSurface]:
    "Canada Post International Parcel Surface",
  [CanadaPostServices.SmallPacketInternationalAir]:
    "Canada Post Small Packet International Air",
  [CanadaPostServices.SmallPacketInternationalSurface]:
    "Canada Post Small Packet International Surface",
  [CanadaPostServices.TrackedPacketInternational]:
    "Canada Post Tracked Packet International",
  [CanadaPostServices.ExpeditedParcelPlus]: "Canada Post Expedited Parcel Plus",
};

export const glsServiceToLabel: Record<GlsServices, string> = {
  [GlsServices.EarlyPriorityOvernight]: "GLS Early Priority Overnight",
  [GlsServices.PriorityOvernight]: "GLS Priority Overnight",
  [GlsServices.CaliforniaParcelService]: "GLS California Parcel Service",
  [GlsServices.SaturdayDeliveryService]: "GLS Saturday Delivery Service",
  [GlsServices.EarlySaturdayService]: "GLS Early Saturday Service",
  [GlsServices.NoonPriorityService]: "GLS Noon Priority Service",
};

export const asendiaServiceToLabel: Record<AsendiaServices, string> = {
  [AsendiaServices.ADS]: "Asendia ADS",
  [AsendiaServices.AirFreightInbound]: "Asendia Air Freight Inbound",
  [AsendiaServices.AirFreightOutbound]: "Asendia Air Freight Outbound",
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterExpedited]:
    "Asendia Domestic Bound Printer Matter Expedited",
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterGround]:
    "Asendia Domestic Bound Printer Matter Ground",
  [AsendiaServices.AsendiaDomesticFlatsExpedited]:
    "Asendia Domestic Flats Expedited",
  [AsendiaServices.AsendiaDomesticFlatsGround]: "Asendia Domestic Flats Ground",
  [AsendiaServices.AsendiaDomesticParcelGroundOver1lb]:
    "Asendia Domestic Parcel Ground Over 1lb",
  [AsendiaServices.AsendiaDomesticParcelGroundUnder1lb]:
    "Asendia Domestic Parcel Ground Under 1lb",
  [AsendiaServices.AsendiaDomesticParcelMAXOver1lb]:
    "Asendia Domestic Parcel MAX Over 1lb",
  [AsendiaServices.AsendiaDomesticParcelMAXUnder1lb]:
    "Asendia Domestic Parcel MAX Under 1lb",
  [AsendiaServices.AsendiaDomesticParcelOver1lbExpedited]:
    "Asendia Domestic Parcel Over 1lb Expedited",
  [AsendiaServices.AsendiaDomesticParcelUnder1lbExpedited]:
    "Asendia Domestic Parcel Under 1lb Expedited",
  [AsendiaServices.AsendiaDomesticPromoParcelExpedited]:
    "Asendia Domestic Promo Parcel Expedited",
  [AsendiaServices.AsendiaDomesticPromoParcelGround]:
    "Asendia Domestic Promo Parcel Ground",
  [AsendiaServices.BulkFreight]: "Asendia Bulk Freight",
  [AsendiaServices.BusinessMailCanadaLettermail]:
    "Asendia Business Mail Canada Lettermail",
  [AsendiaServices.BusinessMailCanadaLettermailMachineable]:
    "Asendia Business Mail Canada Lettermail Machineable",
  [AsendiaServices.BusinessMailEconomy]: "Asendia Business Mail Economy",
  [AsendiaServices.BusinessMailEconomyLPWholesale]:
    "Asendia Business Mail Economy LP Wholesale",
  [AsendiaServices.BusinessMailEconomySPWholesale]:
    "Asendia Business Mail Economy SP Wholesale",
  [AsendiaServices.BusinessMailIPA]: "Asendia Business Mail IPA",
  [AsendiaServices.BusinessMailISAL]: "Asendia Business Mail ISAL",
  [AsendiaServices.BusinessMailPriority]: "Asendia Business Mail Priority",
  [AsendiaServices.BusinessMailPriorityLPWholesale]:
    "Asendia Business Mail Priority LP Wholesale",
  [AsendiaServices.BusinessMailPrioritySPWholesale]:
    "Asendia Business Mail Priority SP Wholesale",
  [AsendiaServices.MarketingMailCanadaPersonalizedLCP]:
    "Asendia Marketing Mail Canada Personalized LCP",
  [AsendiaServices.MarketingMailCanadaPersonalizedMachineable]:
    "Asendia Marketing Mail Canada Personalized Machineable",
  [AsendiaServices.MarketingMailCanadaPersonalizedNDG]:
    "Asendia Marketing Mail Canada Personalized NDG",
  [AsendiaServices.MarketingMailEconomy]: "Asendia Marketing Mail Economy",
  [AsendiaServices.MarketingMailIPA]: "Asendia Marketing Mail IPA",
  [AsendiaServices.MarketingMailISAL]: "Asendia Marketing Mail ISAL",
  [AsendiaServices.MarketingMailPriority]: "Asendia Marketing Mail Priority",
  [AsendiaServices.PublicationsCanadaLCP]: "Asendia Publications Canada LCP",
  [AsendiaServices.PublicationsCanadaNDG]: "Asendia Publications Canada NDG",
  [AsendiaServices.PublicationsEconomy]: "Asendia Publications Economy",
  [AsendiaServices.PublicationsIPA]: "Asendia Publications IPA",
  [AsendiaServices.PublicationsISAL]: "Asendia Publications ISAL",
  [AsendiaServices.PublicationsPriority]: "Asendia Publications Priority",
  [AsendiaServices.ePAQElite]: "Asendia ePAQ Elite",
  [AsendiaServices.ePAQEliteCustom]: "Asendia ePAQ Elite Custom",
  [AsendiaServices.ePAQEliteDAP]: "Asendia ePAQ Elite DAP",
  [AsendiaServices.ePAQEliteDDP]: "Asendia ePAQ Elite DDP",
  [AsendiaServices.ePAQEliteDDPOversized]: "Asendia ePAQ Elite DDP Oversized",
  [AsendiaServices.ePAQEliteDPD]: "Asendia ePAQ Elite DPD",
  [AsendiaServices.ePAQEliteDirectAccessCanadaDDP]:
    "Asendia ePAQ Elite Direct Access Canada DDP",
  [AsendiaServices.ePAQEliteOversized]: "Asendia ePAQ Elite Oversized",
  [AsendiaServices.ePAQPlus]: "Asendia ePAQ Plus",
  [AsendiaServices.ePAQPlusCustom]: "Asendia ePAQ Plus Custom",
  [AsendiaServices.ePAQPlusCustomsPrepaid]: "Asendia ePAQ Plus Customs Prepaid",
  [AsendiaServices.ePAQPlusDAP]: "Asendia ePAQ Plus DAP",
  [AsendiaServices.ePAQPlusDDP]: "Asendia ePAQ Plus DDP",
  [AsendiaServices.ePAQPlusEconomy]: "Asendia ePAQ Plus Economy",
  [AsendiaServices.ePAQPlusWholesale]: "Asendia ePAQ Plus Wholesale",
  [AsendiaServices.ePAQPlusePacket]: "Asendia ePAQ Plus ePacket",
  [AsendiaServices.ePAQPlusePacketCanadaCustomsPrePaid]:
    "Asendia ePAQ Plus ePacket Canada Customs Prepaid",
  [AsendiaServices.ePAQPlusePacketCanadaDDP]:
    "Asendia ePAQ Plus ePacket Canada DDP",
  [AsendiaServices.ePAQReturnsDomestic]: "Asendia ePAQ Returns Domestic",
  [AsendiaServices.ePAQReturnsInternational]:
    "Asendia ePAQ Returns International",
  [AsendiaServices.ePAQSelect]: "Asendia ePAQ Select",
  [AsendiaServices.ePAQSelectCustom]: "Asendia ePAQ Select Custom",
  [AsendiaServices.ePAQSelectCustomsPrepaidByShopper]:
    "Asendia ePAQ Select Customs Prepaid by Shopper",
  [AsendiaServices.ePAQSelectDAP]: "Asendia ePAQ Select DAP",
  [AsendiaServices.ePAQSelectDDP]: "Asendia ePAQ Select DDP",
  [AsendiaServices.ePAQSelectDDPDirectAccess]:
    "Asendia ePAQ Select DDP Direct Access",
  [AsendiaServices.ePAQSelectDirectAccess]: "Asendia ePAQ Select Direct Access",
  [AsendiaServices.ePAQSelectDirectAccessCanadaDDP]:
    "Asendia ePAQ Select Direct Access Canada DDP",
  [AsendiaServices.ePAQSelectEconomy]: "Asendia ePAQ Select Economy",
  [AsendiaServices.ePAQSelectOversized]: "Asendia ePAQ Select Oversized",
  [AsendiaServices.ePAQSelectOversizedDDP]: "Asendia ePAQ Select Oversized DDP",
  [AsendiaServices.ePAQSelectPMEI]: "Asendia ePAQ Select PMEI",
  [AsendiaServices.ePAQSelectPMEICanadaCustomsPrePaid]:
    "Asendia ePAQ Select PMEI Canada Customs Prepaid",
  [AsendiaServices.ePAQSelectPMEIPCPostage]:
    "Asendia ePAQ Select PMEI PC Postage",
  [AsendiaServices.ePAQSelectPMI]: "Asendia ePAQ Select PMI",
  [AsendiaServices.ePAQSelectPMICanadaCustomsPrepaid]:
    "Asendia ePAQ Select PMI Canada Customs Prepaid",
  [AsendiaServices.ePAQSelectPMICanadaDDP]:
    "Asendia ePAQ Select PMI Canada DDP",
  [AsendiaServices.ePAQSelectPMINonPresort]:
    "Asendia ePAQ Select PMI Non-Presort",
  [AsendiaServices.ePAQSelectPMIPCPostage]:
    "Asendia ePAQ Select PMI PC Postage",
  [AsendiaServices.ePAQStandard]: "Asendia ePAQ Standard",
  [AsendiaServices.ePAQStandardCustom]: "Asendia ePAQ Standard Custom",
  [AsendiaServices.ePAQStandardEconomy]: "Asendia ePAQ Standard Economy",
  [AsendiaServices.ePAQStandardIPA]: "Asendia ePAQ Standard IPA",
  [AsendiaServices.ePAQStandardISAL]: "Asendia ePAQ Standard ISAL",
  [AsendiaServices.ePaqSelectPMEINonPresort]:
    "Asendia ePAQ Select PMEI Non-Presort",
  [AsendiaServices.ePAQSelectDDPDG]: "Asendia ePAQ Select DDP DG",
  [AsendiaServices.AsendiaDomesticParcelMaxUnder70lb]:
    "Asendia Domestic Parcel Max Under 70lb",
};

export const royalMailV3ServiceToLabel: Record<RoyalMailV3Services, string> = {
  [RoyalMailV3Services.RoyalMailV3]: "Royal Mail V3",
  [RoyalMailV3Services.DEImportRoyalMail24PlusParcelsWeekendService]:
    "Royal Mail DE Import 24 Plus Parcels Weekend Service",
  [RoyalMailV3Services.DEImportStandard24LargeLetter]:
    "Royal Mail DE Import Standard 24 Large Letter",
  [RoyalMailV3Services.DEImportStandard24LargeLetterWeekendService]:
    "Royal Mail DE Import Standard 24 Large Letter Weekend Service",
  [RoyalMailV3Services.DEImportStandard24Parcel]:
    "Royal Mail DE Import Standard 24 Parcel",
  [RoyalMailV3Services.DEImportStandard24ParcelWeekendService]:
    "Royal Mail DE Import Standard 24 Parcel Weekend Service",
  [RoyalMailV3Services.DEImportStandard48LargeLetter]:
    "Royal Mail DE Import Standard 48 Large Letter",
  [RoyalMailV3Services.DEImportStandard48LargeLetterWeekendService]:
    "Royal Mail DE Import Standard 48 Large Letter Weekend Service",
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableHighVolumeWeekendService]:
    "Royal Mail DE Import Tracked 24 Parcel Boxable High Volume Weekend Service",
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableWeekendService]:
    "Royal Mail DE Import Tracked 24 Parcel Boxable Weekend Service",
  [RoyalMailV3Services.DEImportTracked24ParcelHighVolumeWeekendService]:
    "Royal Mail DE Import Tracked 24 Parcel High Volume Weekend Service",
  [RoyalMailV3Services.DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE]:
    "Royal Mail DE Import Tracked 24 Signature Parcel High Volume Weekend Service AGE",
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableHighVolumeWeekendService]:
    "Royal Mail DE Import Tracked 48 Parcel Boxable High Volume Weekend Service",
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableWeekendService]:
    "Royal Mail DE Import Tracked 48 Parcel Boxable Weekend Service",
  [RoyalMailV3Services.DEImportTracked48ParcelHighVolumeWeekendService]:
    "Royal Mail DE Import Tracked 48 Parcel High Volume Weekend Service",
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeAGE]:
    "Royal Mail DE Import Tracked 48 Signature Parcel High Volume AGE",
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE]:
    "Royal Mail DE Import Tracked 48 Signature Parcel High Volume Weekend Service AGE",
  [RoyalMailV3Services.DEImporttoEUMaxSortLargeLetter]:
    "Royal Mail DE Import to EU Max Sort Large Letter",
  [RoyalMailV3Services.DEImporttoEUMaxSortParcel]:
    "Royal Mail DE Import to EU Max Sort Parcel",
  [RoyalMailV3Services.DEImporttoEUSignedParcel]:
    "Royal Mail DE Import to EU Signed Parcel",
  [RoyalMailV3Services.DEImporttoEUTrackedHighVolLargeLetter]:
    "Royal Mail DE Import to EU Tracked High Volume Large Letter",
  [RoyalMailV3Services.DEImporttoEUTrackedParcel]:
    "Royal Mail DE Import to EU Tracked Parcel",
  [RoyalMailV3Services.DEImporttoEUTrackedSignedLargeLetter]:
    "Royal Mail DE Import to EU Tracked Signed Large Letter",
  [RoyalMailV3Services.DEImporttoEUTrackedSignedParcel]:
    "Royal Mail DE Import to EU Tracked Signed Parcel",
  [RoyalMailV3Services.HMForcesMail]: "Royal Mail HM Forces Mail",
  [RoyalMailV3Services.HMForcesSignedFor]: "Royal Mail HM Forces Signed For",
  [RoyalMailV3Services.HMForcesSpecialDelivery1000]:
    "Royal Mail HM Forces Special Delivery £1000",
  [RoyalMailV3Services.HMForcesSpecialDelivery2500]:
    "Royal Mail HM Forces Special Delivery £2500",
  [RoyalMailV3Services.HMForcesSpecialDelivery500]:
    "Royal Mail HM Forces Special Delivery £500",
  [RoyalMailV3Services.ImportDETracked24]: "Royal Mail Import DE Tracked 24",
  [RoyalMailV3Services.ImportDETracked24HighVolume]:
    "Royal Mail Import DE Tracked 24 High Volume",
  [RoyalMailV3Services.ImportDETracked24Letterboxable]:
    "Royal Mail Import DE Tracked 24 Letterboxable",
  [RoyalMailV3Services.ImportDETracked24LetterboxableHighVolume]:
    "Royal Mail Import DE Tracked 24 Letterboxable High Volume",
  [RoyalMailV3Services.ImportDETracked24SignatureHighVolumeAGE]:
    "Royal Mail Import DE Tracked 24 Signature High Volume AGE",
  [RoyalMailV3Services.ImportDETracked24SignatureStandardAGE]:
    "Royal Mail Import DE Tracked 24 Signature Standard AGE",
  [RoyalMailV3Services.ImportDETracked48]: "Royal Mail Import DE Tracked 48",
  [RoyalMailV3Services.ImportDETracked48HighVolume]:
    "Royal Mail Import DE Tracked 48 High Volume",
  [RoyalMailV3Services.ImportDETracked48Letterboxable]:
    "Royal Mail Import DE Tracked 48 Letterboxable",
  [RoyalMailV3Services.ImportDETracked48LetterboxableHighVolume]:
    "Royal Mail Import DE Tracked 48 Letterboxable High Volume",
  [RoyalMailV3Services.ImportDETrackedReturns24]:
    "Royal Mail Import DE Tracked Returns 24",
  [RoyalMailV3Services.ImportDETrackedReturns48]:
    "Royal Mail Import DE Tracked Returns 48",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriority]:
    "Royal Mail International Business Mail Large Letter Country Priced Priority",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriorityETOE]:
    "Royal Mail International Business Mail Large Letter Country Priced Priority ETOE",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountrySortPriority]:
    "Royal Mail International Business Mail Large Letter Country Sort Priority",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortPriorityService]:
    "Royal Mail International Business Mail Large Letter Max Sort Priority Service",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortResidueStandard]:
    "Royal Mail International Business Mail Large Letter Max Sort Residue Standard",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortStandardService]:
    "Royal Mail International Business Mail Large Letter Max Sort Standard Service",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterSemiTracked]:
    "Royal Mail International Business Mail Large Letter Semi-Tracked",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriority]:
    "Royal Mail International Business Mail Large Letter Zero Sort Priority",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriorityMachine]:
    "Royal Mail International Business Mail Large Letter Zero Sort Priority Machine",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriority]:
    "Royal Mail International Business Mail Large Letter Zone Sort Priority",
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriorityMachine]:
    "Royal Mail International Business Mail Large Letter Zone Sort Priority Machine",
  [RoyalMailV3Services.InternationalBusinessMailLetterMaxSortResidueStandard]:
    "Royal Mail International Business Mail Letter Max Sort Residue Standard",
  [RoyalMailV3Services.InternationalBusinessMailLettersMaxSortStandard]:
    "Royal Mail International Business Mail Letters Max Sort Standard",
  [RoyalMailV3Services.InternationalBusinessMailLettersZeroSortPriority]:
    "Royal Mail International Business Mail Letters Zero Sort Priority",
  [RoyalMailV3Services.InternationalBusinessMailLettersZoneSortPriority]:
    "Royal Mail International Business Mail Letters Zone Sort Priority",
  [RoyalMailV3Services.InternationalBusinessMailSignedCountryPriced]:
    "Royal Mail International Business Mail Signed Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailSignedExtraCompensationCountryPriced]:
    "Royal Mail International Business Mail Signed Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailSignedHighVolCountryPriced]:
    "Royal Mail International Business Mail Signed High Volume Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailSignedZoneSort]:
    "Royal Mail International Business Mail Signed Zone Sort",
  [RoyalMailV3Services.InternationalBusinessMailTrackedCountryPriced]:
    "Royal Mail International Business Mail Tracked Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolCountryPriced]:
    "Royal Mail International Business Mail Tracked High Volume Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolExtraCompCountryPriced]:
    "Royal Mail International Business Mail Tracked High Volume Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedLargeLetterCountryPricedETOE]:
    "Royal Mail International Business Mail Tracked Large Letter Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedCountryPriced]:
    "Royal Mail International Business Mail Tracked Signed Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolCountryPriced]:
    "Royal Mail International Business Mail Tracked Signed High Volume Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced]:
    "Royal Mail International Business Mail Tracked Signed High Volume Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE]:
    "Royal Mail International Business Mail Tracked Signed Large Letter Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedZoneSort]:
    "Royal Mail International Business Mail Tracked Signed Zone Sort",
  [RoyalMailV3Services.InternationalBusinessMailTrackedZoneSort]:
    "Royal Mail International Business Mail Tracked Zone Sort",
  [RoyalMailV3Services.InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE]:
    "Royal Mail International Business PC Tracked Signed Large Letter HV Extra Compensation Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxable]:
    "Royal Mail International Business Parcel Priority Country Priced Boxable",
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxableDDP]:
    "Royal Mail International Business Parcel Priority Country Priced Boxable DDP",
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPriced]:
    "Royal Mail International Business Parcel Tracked Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxable]:
    "Royal Mail International Business Parcel Tracked Country Priced Boxable",
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableDDP]:
    "Royal Mail International Business Parcel Tracked Country Priced Boxable DDP",
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp]:
    "Royal Mail International Business Parcel Tracked Country Priced Boxable Extra Compensation",
  [RoyalMailV3Services.InternationalBusinessParcels]:
    "Royal Mail International Business Parcels",
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectPriority]:
    "Royal Mail International Business Parcels Print Direct Priority",
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectStandard]:
    "Royal Mail International Business Parcels Print Direct Standard",
  [RoyalMailV3Services.InternationalBusinessParcelsSignedCountryPriced]:
    "Royal Mail International Business Parcels Signed Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationCountryPriced]:
    "Royal Mail International Business Parcels Signed Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationZoneSort]:
    "Royal Mail International Business Parcels Signed Extra Compensation Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsSignedZoneSort]:
    "Royal Mail International Business Parcels Signed Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPriced]:
    "Royal Mail International Business Parcels Tracked Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPricedETOE]:
    "Royal Mail International Business Parcels Tracked Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedDirectIrelandCountry]:
    "Royal Mail International Business Parcels Tracked Direct Ireland Country",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompCountryPriced]:
    "Royal Mail International Business Parcels Tracked Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompZoneSort]:
    "Royal Mail International Business Parcels Tracked Extra Compensation Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPriced]:
    "Royal Mail International Business Parcels Tracked Signed Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPricedETOE]:
    "Royal Mail International Business Parcels Tracked Signed Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedDDP]:
    "Royal Mail International Business Parcels Tracked Signed DDP",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced]:
    "Royal Mail International Business Parcels Tracked Signed Extra Compensation Country Priced",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort]:
    "Royal Mail International Business Parcels Tracked Signed Extra Compensation Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedZoneSort]:
    "Royal Mail International Business Parcels Tracked Signed Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedZoneSort]:
    "Royal Mail International Business Parcels Tracked Zone Sort",
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriority]:
    "Royal Mail International Business Parcels Zero Sort Priority",
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriorityETOE]:
    "Royal Mail International Business Parcels Zero Sort Priority ETOE",
  [RoyalMailV3Services.InternationalBusinessParcelsZoneSortPriorityService]:
    "Royal Mail International Business Parcels Zone Sort Priority Service",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE]:
    "Royal Mail International Business Personal Correspondence Large Letter Country Sort Priority ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked]:
    "Royal Mail International Business Personal Correspondence Letter Priority Untracked",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE]:
    "Royal Mail International Business Personal Correspondence Letter Priority Untracked ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE]:
    "Royal Mail International Business Personal Correspondence Letter Tracked High Volume Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE]:
    "Royal Mail International Business Personal Correspondence Letter Tracked Signed High Volume Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortLetter]:
    "Royal Mail International Business Personal Correspondence Max Sort Letter",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter]:
    "Royal Mail International Business Personal Correspondence Max Sort Residue Large Letter",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter]:
    "Royal Mail International Business Personal Correspondence Max Sort Residue Letter",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE]:
    "Royal Mail International Business Personal Correspondence Signed Large Letter Extra Compensation Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE]:
    "Royal Mail International Business Personal Correspondence Signed Letter High Volume Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE]:
    "Royal Mail International Business Personal Correspondence Tracked Large Letter High Volume Extra Compensation Country Priced ETOE",
  [RoyalMailV3Services.InternationalBusinessPrintedMatterPacket]:
    "Royal Mail International Business Printed Matter Packet",
  [RoyalMailV3Services.InternationalBusinessTrackedExpressNPC]:
    "Royal Mail International Business Tracked Express NPC",
  [RoyalMailV3Services.InternationalEconomyOnAccount]:
    "Royal Mail International Economy On Account",
  [RoyalMailV3Services.InternationalSignedOnAccount]:
    "Royal Mail International Signed On Account",
  [RoyalMailV3Services.InternationalSignedOnAccountExtraComp]:
    "Royal Mail International Signed On Account Extra Compensation",
  [RoyalMailV3Services.InternationalStandardOnAccount]:
    "Royal Mail International Standard On Account",
  [RoyalMailV3Services.InternationalTrackedOnAccount]:
    "Royal Mail International Tracked On Account",
  [RoyalMailV3Services.InternationalTrackedOnAccountExtraComp]:
    "Royal Mail International Tracked On Account Extra Compensation",
  [RoyalMailV3Services.InternationalTrackedParcels30kg]:
    "Royal Mail International Tracked Parcels 30kg",
  [RoyalMailV3Services.InternationalTrackedParcels30kgCPrio]:
    "Royal Mail International Tracked Parcels 30kg Country Priority",
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEC]:
    "Royal Mail International Tracked Parcels 30kg ETOE Country",
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEE]:
    "Royal Mail International Tracked Parcels 30kg ETOE Economy",
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraComp]:
    "Royal Mail International Tracked Parcels 30kg Extra Compensation",
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEC]:
    "Royal Mail International Tracked Parcels 30kg Extra Compensation ETOE Country",
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEE]:
    "Royal Mail International Tracked Parcels 30kg Extra Compensation ETOE Economy",
  [RoyalMailV3Services.InternationalTrackedParcels30kgXCompCPrio]:
    "Royal Mail International Tracked Parcels 30kg Extra Compensation Country Priority",
  [RoyalMailV3Services.InternationalTrackedSignedOnAccount]:
    "Royal Mail International Tracked Signed On Account",
  [RoyalMailV3Services.InternationalTrackedSignedOnAccountExtraComp]:
    "Royal Mail International Tracked Signed On Account Extra Compensation",
  [RoyalMailV3Services.ParcelpostFlatRateAnnual]:
    "Royal Mail Parcel Post Flat Rate Annual",
  [RoyalMailV3Services.RM24LargeLetterAnnualFlatRate]:
    "Royal Mail 24 Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RM24PresortedLargeLetterAnnualFlatRate]:
    "Royal Mail 24 Presorted Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RM24PresortedParcelsAnnualFlatRate]:
    "Royal Mail 24 Presorted Parcels Annual Flat Rate",
  [RoyalMailV3Services.RM48LargeLetterAnnualFlatRate]:
    "Royal Mail 48 Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RM48PresortedLargeLetterAnnualFlatRate]:
    "Royal Mail 48 Presorted Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RM48PresortedParcelsAnnualFlatRate]:
    "Royal Mail 48 Presorted Parcels Annual Flat Rate",
  [RoyalMailV3Services.RoyalMail1stClass]: "Royal Mail 1st Class",
  [RoyalMailV3Services.RoyalMail1stClassLettersDailyRateservice]:
    "Royal Mail 1st Class Letters Daily Rate Service",
  [RoyalMailV3Services.RoyalMail1stClassSignedFor]:
    "Royal Mail 1st Class Signed For",
  [RoyalMailV3Services.RoyalMail24LargeLetterDailyRate]:
    "Royal Mail 24 Large Letter Daily Rate",
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRate]:
    "Royal Mail 24 Large Letter Flat Rate",
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRateService]:
    "Royal Mail 24 Large Letter Flat Rate Service",
  [RoyalMailV3Services.RoyalMail24PacketpostFlatRateService]:
    "Royal Mail 24 Packet Post Flat Rate Service",
  [RoyalMailV3Services.RoyalMail24ParcelDailyRateService]:
    "Royal Mail 24 Parcel Daily Rate Service",
  [RoyalMailV3Services.RoyalMail24ParcelSort8DailyRateService]:
    "Royal Mail 24 Parcel Sort 8 Daily Rate Service",
  [RoyalMailV3Services.RoyalMail24ParcelSort8FlatRateService]:
    "Royal Mail 24 Parcel Sort 8 Flat Rate Service",
  [RoyalMailV3Services.RoyalMail24ParcelsDailyRate]:
    "Royal Mail 24 Parcels Daily Rate",
  [RoyalMailV3Services.RoyalMail24ParcelsFlatRate]:
    "Royal Mail 24 Parcels Flat Rate",
  [RoyalMailV3Services.RoyalMail24PresortedLargeLetter]:
    "Royal Mail 24 Presorted Large Letter",
  [RoyalMailV3Services.RoyalMail24PresortedParcels]:
    "Royal Mail 24 Presorted Parcels",
  [RoyalMailV3Services.RoyalMail24SORT8LargeLetterAnnualFlatRate]:
    "Royal Mail 24 Sort 8 Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RoyalMail24SORT8ParcelsAnnualFlatRate]:
    "Royal Mail 24 Sort 8 Parcels Annual Flat Rate",
  [RoyalMailV3Services.RoyalMail2ndClass]: "Royal Mail 2nd Class",
  [RoyalMailV3Services.RoyalMail2ndClassLettersDailyRateservice]:
    "Royal Mail 2nd Class Letters Daily Rate Service",
  [RoyalMailV3Services.RoyalMail2ndClassSignedFor]:
    "Royal Mail 2nd Class Signed For",
  [RoyalMailV3Services.RoyalMail48LargeLetterDailyRate]:
    "Royal Mail 48 Large Letter Daily Rate",
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRate]:
    "Royal Mail 48 Large Letter Flat Rate",
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRateService]:
    "Royal Mail 48 Large Letter Flat Rate Service",
  [RoyalMailV3Services.RoyalMail48PacketpostFlatRateService]:
    "Royal Mail 48 Packet Post Flat Rate Service",
  [RoyalMailV3Services.RoyalMail48ParcelDailyRateService]:
    "Royal Mail 48 Parcel Daily Rate Service",
  [RoyalMailV3Services.RoyalMail48ParcelSort8DailyRateService]:
    "Royal Mail 48 Parcel Sort 8 Daily Rate Service",
  [RoyalMailV3Services.RoyalMail48ParcelSort8FlatRateService]:
    "Royal Mail 48 Parcel Sort 8 Flat Rate Service",
  [RoyalMailV3Services.RoyalMail48ParcelsDailyRate]:
    "Royal Mail 48 Parcels Daily Rate",
  [RoyalMailV3Services.RoyalMail48ParcelsFlatRate]:
    "Royal Mail 48 Parcels Flat Rate",
  [RoyalMailV3Services.RoyalMail48PresortedLargeLetter]:
    "Royal Mail 48 Presorted Large Letter",
  [RoyalMailV3Services.RoyalMail48PresortedParcels]:
    "Royal Mail 48 Presorted Parcels",
  [RoyalMailV3Services.RoyalMail48SORT8LargeLetterAnnualFlatRate]:
    "Royal Mail 48 Sort 8 Large Letter Annual Flat Rate",
  [RoyalMailV3Services.RoyalMail48Sort8ParcelsAnnualFlatRate]:
    "Royal Mail 48 Sort 8 Parcels Annual Flat Rate",
  [RoyalMailV3Services.SpecialDeliveryGuaranteed1000]:
    "Royal Mail Special Delivery Guaranteed £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteed2500]:
    "Royal Mail Special Delivery Guaranteed £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteed750]:
    "Royal Mail Special Delivery Guaranteed £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE1000]:
    "Royal Mail Special Delivery Guaranteed AGE £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE2500]:
    "Royal Mail Special Delivery Guaranteed AGE £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE750]:
    "Royal Mail Special Delivery Guaranteed AGE £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM1000]:
    "Royal Mail Special Delivery Guaranteed By 1PM £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM2500]:
    "Royal Mail Special Delivery Guaranteed By 1PM £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM750]:
    "Royal Mail Special Delivery Guaranteed By 1PM £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE1000]:
    "Royal Mail Special Delivery Guaranteed By 1PM AGE £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE2500]:
    "Royal Mail Special Delivery Guaranteed By 1PM AGE £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE750]:
    "Royal Mail Special Delivery Guaranteed By 1PM AGE £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID1000]:
    "Royal Mail Special Delivery Guaranteed By 1PM ID £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID2500]:
    "Royal Mail Special Delivery Guaranteed By 1PM ID £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID750]:
    "Royal Mail Special Delivery Guaranteed By 1PM ID £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM1000]:
    "Royal Mail Special Delivery Guaranteed By 9AM £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM2500]:
    "Royal Mail Special Delivery Guaranteed By 9AM £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM750]:
    "Royal Mail Special Delivery Guaranteed By 9AM £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE1000]:
    "Royal Mail Special Delivery Guaranteed By 9AM AGE £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE2500]:
    "Royal Mail Special Delivery Guaranteed By 9AM AGE £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE750]:
    "Royal Mail Special Delivery Guaranteed By 9AM AGE £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID1000]:
    "Royal Mail Special Delivery Guaranteed By 9AM ID £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID2500]:
    "Royal Mail Special Delivery Guaranteed By 9AM ID £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID750]:
    "Royal Mail Special Delivery Guaranteed By 9AM ID £750",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID1000]:
    "Royal Mail Special Delivery Guaranteed ID £1000",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID2500]:
    "Royal Mail Special Delivery Guaranteed ID £2500",
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID750]:
    "Royal Mail Special Delivery Guaranteed ID £750",
  [RoyalMailV3Services.Tracked24]: "Royal Mail Tracked 24",
  [RoyalMailV3Services.Tracked24HighVolume]:
    "Royal Mail Tracked 24 High Volume",
  [RoyalMailV3Services.Tracked24HighVolumeSignatureAGE]:
    "Royal Mail Tracked 24 High Volume Signature AGE",
  [RoyalMailV3Services.Tracked24SignatureAGE]:
    "Royal Mail Tracked 24 Signature AGE",
  [RoyalMailV3Services.Tracked48]: "Royal Mail Tracked 48",
  [RoyalMailV3Services.Tracked48HighVolume]:
    "Royal Mail Tracked 48 High Volume",
  [RoyalMailV3Services.Tracked48HighVolumeSignatureAGE]:
    "Royal Mail Tracked 48 High Volume Signature AGE",
  [RoyalMailV3Services.Tracked48SignatureAGE]:
    "Royal Mail Tracked 48 Signature AGE",
  [RoyalMailV3Services.TrackedLetterBoxable24]:
    "Royal Mail Tracked Letter Boxable 24",
  [RoyalMailV3Services.TrackedLetterBoxable24HighVolume]:
    "Royal Mail Tracked Letter Boxable 24 High Volume",
  [RoyalMailV3Services.TrackedLetterBoxable48]:
    "Royal Mail Tracked Letter Boxable 48",
  [RoyalMailV3Services.TrackedLetterBoxable48HighVolume]:
    "Royal Mail Tracked Letter Boxable 48 High Volume",
  [RoyalMailV3Services.TrackedReturns24]: "Royal Mail Tracked Returns 24",
  [RoyalMailV3Services.TrackedReturns48]: "Royal Mail Tracked Returns 48",
};

export const passportGlobalServiceToLabel: Record<
  PassportGlobalServices,
  string
> = {
  [PassportGlobalServices.PriorityDdpDelcon]:
    "Priority DDP Delivery Confirmation",
  [PassportGlobalServices.PriorityDdp]: "Priority DDP",
  [PassportGlobalServices.epacketDdp]: "ePacket DDP",
  [PassportGlobalServices.ExpressDdp]: "Express DDP",
  [PassportGlobalServices.PriorityDduDelcon]:
    "Priority DDU Delivery Confirmation",
  [PassportGlobalServices.PriorityDdu]: "Priority DDU",
  [PassportGlobalServices.ExpeditedDdpDelcon]:
    "Expedited DDP Delivery Confirmation",
  [PassportGlobalServices.ExpressDdpDelcon]:
    "Express DDP Delivery Confirmation",
  [PassportGlobalServices.GlobalPlus]: "Global Plus",
};

export const speeDeeServiceToLabel: Record<SpeeDeeServices, string> = {
  [SpeeDeeServices.SpeeDeeDelivery]: "SpeeDee Delivery",
};

export const landmarkGlobalServiceToLabel: Record<
  LandmarkGlobalServices,
  string
> = { [LandmarkGlobalServices.Delivery]: "Landmark Global Delivery" };

export const etaExpressServiceToLabel: Record<ETAExpressServices, string> = {
  [ETAExpressServices.ETAExpress]: "ETA Express",
};

export const epostGlobalServiceToLabel: Record<EPostGlobalServices, string> = {
  [EPostGlobalServices.CourierService]: "Courier Service",
  [EPostGlobalServices.CourierServiceDutiesPaid]: "Courier Service Duties Paid",
  [EPostGlobalServices.DomesticPriorityParcel]: "Domestic Priority Parcel",
  [EPostGlobalServices.DomesticeDGE]: "Domestic eDGE",
  [EPostGlobalServices.EconomyParcel]: "Economy Parcel",
  [EPostGlobalServices.ExpeditedParcelWithDeliveryConfirmationDutiesPaid]:
    "Expedited Parcel With Delivery Confirmation Duties Paid",
  [EPostGlobalServices.ExpeditedWithDC]: "Expedited With DC",
  [EPostGlobalServices.FirstClassParcelsInternational]:
    "First Class Parcels International",
  [EPostGlobalServices.IPA]: "IPA",
  [EPostGlobalServices.ISAL]: "ISAL",
  [EPostGlobalServices.PMEI]: "PMEI",
  [EPostGlobalServices.PMEIDDP]: "PMEI DDP",
  [EPostGlobalServices.PMIPresortService]: "PMI Presort Service",
  [EPostGlobalServices.PMIServiceDutyPaid]: "PMI Service Duty Paid",
  [EPostGlobalServices.PriorityPacketDDU]: "Priority Packet DDU",
  [EPostGlobalServices.PriorityParcelNoDeliveryConfirmation]:
    "Priority Parcel No Delivery Confirmation",
  [EPostGlobalServices.PriorityParcelNoDeliveryConfirmationDutiesPaid]:
    "Priority Parcel No Delivery Confirmation Duties Paid",
  [EPostGlobalServices.PriorityParcelWithDeliveryConfirmation]:
    "Priority Parcel With Delivery Confirmation",
  [EPostGlobalServices.PriorityParcelWithDeliveryConfirmationDutiesPaid]:
    "Priority Parcel With Delivery Confirmation Duties Paid",
  [EPostGlobalServices.ePacket]: "ePacket",
  [EPostGlobalServices.ePacketDDP]: "ePacket DDP",
};

export const crossborderlyServiceToLabel: Record<
  CrossborderlyServices,
  string
> = { [CrossborderlyServices.Crossborderly]: "Crossborderly" };

export const getServiceLabel = (service: Service) => {
  if (isFedexService(service)) {
    return fedexServiceToLabel[service];
  } else if (isUpsService(service)) {
    return upsServiceToLabel[service];
  } else if (isUspsService(service)) {
    return uspsServiceToLabel[service];
  } else if (isAmazonService(service)) {
    return amazonServiceToLabel[service];
  } else if (isDhlExpressService(service)) {
    return dhlExpressServiceToLabel[service];
  } else if (isVehoService(service)) {
    return vehoServiceToLabel[service];
  } else if (isApcService(service)) {
    return apcServiceToLabel[service];
  } else if (isFirstMileService(service)) {
    return firstMileServiceToLabel[service];
  } else if (isDhlEcsService(service)) {
    return dhlEcsServiceToLabel[service];
  } else if (isAustraliaPostService(service)) {
    return australiaPostServiceToLabel[service];
  } else if (isCanadaPostService(service)) {
    return canadaPostServiceToLabel[service];
  } else if (isGlsService(service)) {
    return glsServiceToLabel[service];
  } else if (isAsendiaService(service)) {
    return asendiaServiceToLabel[service];
  } else if (isRoyalMailV3Service(service)) {
    return royalMailV3ServiceToLabel[service];
  } else if (isSpeeDeeService(service)) {
    return speeDeeServiceToLabel[service];
  } else if (isPassportGlobalService(service)) {
    return passportGlobalServiceToLabel[service];
  } else if (isLandmarkGlobalService(service)) {
    return landmarkGlobalServiceToLabel[service];
  } else if (isETAExpressService(service)) {
    return etaExpressServiceToLabel[service];
  } else if (isEPostGlobalService(service)) {
    return epostGlobalServiceToLabel[service];
  } else if (isCrossborderlyService(service)) {
    return crossborderlyServiceToLabel[service];
  }
  assertNever(service);
};

export function validateCarrierService(
  carrier: Carriers,
  service: Service,
):
  | {
      carrier: Carriers.FEDEX | Carriers.FEDEX_SMART_POST;
      service: FedexServices;
    }
  | { carrier: Carriers.DHL_EXPRESS; service: DhlExpressServices }
  | { carrier: Carriers.VEHO; service: VehoServices }
  | { carrier: Carriers.UPS | Carriers.UPS_SUREPOST; service: UpsServices }
  | { carrier: Carriers.AMAZON; service: AmazonServices }
  | {
      carrier: Carriers.USPS | Carriers.USPS_RETURNS | Carriers.USPS_SHIP;
      service: UspsServices;
    }
  | { carrier: Carriers.FIRST_MILE; service: FirstMileServices }
  | { carrier: Carriers.DHL_ECS; service: DhlEcsServices }
  | { carrier: Carriers.APC; service: ApcServices }
  | { carrier: Carriers.AUSTRALIA_POST; service: AustraliaPostServices }
  | { carrier: Carriers.CANADA_POST; service: CanadaPostServices }
  | { carrier: Carriers.GLS; service: GlsServices }
  | { carrier: Carriers.ASENDIA; service: AsendiaServices }
  | { carrier: Carriers.ROYAL_MAIL_V3; service: RoyalMailV3Services }
  | { carrier: Carriers.SPEE_DEE; service: SpeeDeeServices }
  | { carrier: Carriers.PASSPORT_GLOBAL; service: PassportGlobalServices }
  | { carrier: Carriers.LANDMARK_GLOBAL; service: LandmarkGlobalServices }
  | { carrier: Carriers.ETA_EXPRESS; service: ETAExpressServices }
  | { carrier: Carriers.EPOST_GLOBAL; service: EPostGlobalServices }
  | { carrier: Carriers.CROSSBORDERLY; service: CrossborderlyServices } {
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST: {
      if (isFedexService(service)) {
        return { carrier, service };
      }

      throw new Error(`Invalid FedEx service: ${service}`);
    }
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST: {
      if (isUpsService(service)) {
        return { carrier, service };
      }

      throw new Error(`Invalid UPS service: ${service}`);
    }
    case Carriers.USPS:
    case Carriers.USPS_RETURNS:
    case Carriers.USPS_SHIP: {
      if (isUspsService(service)) {
        return { carrier, service };
      }

      throw new Error(`Invalid USPS service: ${service}`);
    }
    case Carriers.AMAZON: {
      if (isAmazonService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Amazon service: ${service}`);
    }
    case Carriers.DHL_EXPRESS: {
      if (isDhlExpressService(service)) {
        return { carrier, service };
      }

      throw new Error(`Invalid DHL Express service: ${service}`);
    }
    case Carriers.VEHO: {
      if (isVehoService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Veho service: ${service}`);
    }
    case Carriers.APC: {
      if (isApcService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid APC service: ${service}`);
    }
    case Carriers.FIRST_MILE: {
      if (isFirstMileService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid First Mile service: ${service}`);
    }
    case Carriers.DHL_ECS: {
      if (isDhlEcsService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid DHL ECS service: ${service}`);
    }
    case Carriers.AUSTRALIA_POST: {
      if (isAustraliaPostService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Australia Post service: ${service}`);
    }
    case Carriers.CANADA_POST: {
      if (isCanadaPostService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Canada Post service: ${service}`);
    }
    case Carriers.GLS: {
      if (isGlsService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid GLS service: ${service}`);
    }
    case Carriers.ASENDIA: {
      if (isAsendiaService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Asendia service: ${service}`);
    }
    case Carriers.ROYAL_MAIL_V3: {
      if (isRoyalMailV3Service(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Royal Mail V3 service: ${service}`);
    }
    case Carriers.SPEE_DEE: {
      if (isSpeeDeeService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid SpeeDee service: ${service}`);
    }
    case Carriers.PASSPORT_GLOBAL: {
      if (isPassportGlobalService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Passport Global service: ${service}`);
    }
    case Carriers.LANDMARK_GLOBAL: {
      if (isLandmarkGlobalService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Landmark Global service: ${service}`);
    }
    case Carriers.ETA_EXPRESS: {
      if (isETAExpressService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid ETA Express service: ${service}`);
    }
    case Carriers.EPOST_GLOBAL: {
      if (isEPostGlobalService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid ePost Global service: ${service}`);
    }
    case Carriers.CROSSBORDERLY: {
      if (isCrossborderlyService(service)) {
        return { carrier, service };
      }
      throw new Error(`Invalid Crossborderly service: ${service}`);
    }
    default:
      assertNever(carrier);
  }
}

// We currently allow 22 characters per line (Outbound labels)
export const LabelCustomLineNumbers: Record<Carriers, number> = {
  [Carriers.FEDEX]: 1,
  [Carriers.FEDEX_SMART_POST]: 1,
  [Carriers.UPS]: 2,
  [Carriers.UPS_SUREPOST]: 2,
  [Carriers.USPS]: 3,
  [Carriers.USPS_RETURNS]: 3,
  [Carriers.USPS_SHIP]: 3,
  [Carriers.AMAZON]: 1,
  [Carriers.DHL_EXPRESS]: 1,
  [Carriers.VEHO]: 1,
  [Carriers.APC]: 1,
  [Carriers.FIRST_MILE]: 1,
  [Carriers.DHL_ECS]: 1,
  [Carriers.AUSTRALIA_POST]: 1,
  [Carriers.CANADA_POST]: 1,
  [Carriers.GLS]: 1,
  [Carriers.ASENDIA]: 1,
  [Carriers.ROYAL_MAIL_V3]: 1,
  [Carriers.SPEE_DEE]: 1,
  [Carriers.PASSPORT_GLOBAL]: 1,
  [Carriers.EPOST_GLOBAL]: 1,
  [Carriers.LANDMARK_GLOBAL]: 1,
  [Carriers.ETA_EXPRESS]: 1,
  [Carriers.CROSSBORDERLY]: 1,
};

export const SupportsMultiParcelRates: Record<Carrier, boolean> = {
  [Carriers.FEDEX]: true,
  [Carriers.FEDEX_SMART_POST]: false,
  [Carriers.UPS]: true,
  [Carriers.UPS_SUREPOST]: false,
  [Carriers.USPS]: false,
  [Carriers.USPS_RETURNS]: false,
  [Carriers.USPS_SHIP]: false,
  [Carriers.AMAZON]: false,
  [Carriers.DHL_EXPRESS]: true,
  [Carriers.DHL_ECS]: false,
  [Carriers.VEHO]: false,
  [Carriers.FIRST_MILE]: false,
  [Carriers.APC]: false,
  [Carriers.AUSTRALIA_POST]: true,
  [Carriers.CANADA_POST]: false,
  [Carriers.GLS]: false,
  [Carriers.ASENDIA]: false,
  [Carriers.ROYAL_MAIL_V3]: false,
  [Carriers.SPEE_DEE]: false,
  [Carriers.PASSPORT_GLOBAL]: false,
  [Carriers.EPOST_GLOBAL]: false,
  [Carriers.LANDMARK_GLOBAL]: false,
  [Carriers.ETA_EXPRESS]: false,
  [Carriers.CROSSBORDERLY]: true,
};

export function getPotentialTrackingCodesFromLabelBarcode({
  barcodeValue,
  stripNonNumericCharacters,
}: {
  barcodeValue: string;
  stripNonNumericCharacters: boolean;
}): string[] {
  const trackingCodes = [barcodeValue];
  const barcodeNumbers = stripNonNumericCharacters
    ? barcodeValue.replace(/\D/g, "")
    : barcodeValue;
  for (const [pattern, replacement] of barcodeTransforms) {
    if (barcodeNumbers.match(pattern)) {
      trackingCodes.push(barcodeNumbers.replace(pattern, replacement));
    }
  }

  return trackingCodes;
}

export const barcodeTransforms: [RegExp, string][] = [
  // FedEx barcode starts with 96 and the last 12 characters are the tracking code
  [/^96.*(.{12})$/, "$1"],
  // USPS barcode starts with 420 and the last 22 characters are the tracking code
  [/^420.*(.{22})$/, "$1"],
  // FedExSmartPost labels have USPS barcodes that start with 420 and the last
  // 20 characters are the tracking code
  [/^420.*(.{20})$/, "$1"],
  // FedExSmartPost labels also have barcodes that start with 92 and the last
  // 20 characters are the tracking code
  [/^92.*(.{20})$/, "$1"],
  // Happy Return Code starts with HR and all characters before the first dash is the code
  [/(^HR[^-]*).*/i, "$1"],
];

// Direct UPS service codes
export enum UpsDirectServiceCode {
  NextDayAir = "01",
  TwoDayAir = "02",
  Ground = "03",
  ThreeDaySelect = "12",
  NextDayAirSaver = "13",
  NextDayAirEarly = "14",
  TwoDayAirAM = "59",
  WorldwideExpress = "07",
  WorldwideExpedited = "08",
  WorldwideExpressPlus = "54",
  Standard = "11",
  Saver = "65",
  SurePostUnder1Lb = "92",
  SurePostOver1Lb = "93",
}

export const DirectUpsServiceCodeToService: Record<
  UpsDirectServiceCode,
  UpsServices
> = {
  [UpsDirectServiceCode.NextDayAir]: UpsServices.NextDayAir,
  [UpsDirectServiceCode.TwoDayAir]: UpsServices.SecondDayAir,
  [UpsDirectServiceCode.Ground]: UpsServices.Ground,
  [UpsDirectServiceCode.ThreeDaySelect]: UpsServices.ThreeDaySelect,
  [UpsDirectServiceCode.NextDayAirSaver]: UpsServices.NextDayAirSaver,
  [UpsDirectServiceCode.NextDayAirEarly]: UpsServices.NextDayAirEarlyAM,
  [UpsDirectServiceCode.TwoDayAirAM]: UpsServices.SecondDayAirAM,
  [UpsDirectServiceCode.Standard]: UpsServices.UPSStandard,
  [UpsDirectServiceCode.Saver]: UpsServices.WorldWideSaver,
  [UpsDirectServiceCode.WorldwideExpress]: UpsServices.WorldWideExpress,
  [UpsDirectServiceCode.WorldwideExpedited]: UpsServices.WorldWideExpedited,
  [UpsDirectServiceCode.WorldwideExpressPlus]: UpsServices.WorldWideExpressPlus,
  [UpsDirectServiceCode.SurePostOver1Lb]: UpsServices.SurePostOver1Lb,
  [UpsDirectServiceCode.SurePostUnder1Lb]: UpsServices.SurePostUnder1Lb,
};

// Intentionally not using UpsServices enum because we have services from Easypost UPS that are not in the direct UPS service codes, ex: SurePost
export const UpsServiceToDirectUpsServiceCode: Record<
  string,
  UpsDirectServiceCode
> = {
  [UpsServices.NextDayAir]: UpsDirectServiceCode.NextDayAir,
  [UpsServices.SecondDayAir]: UpsDirectServiceCode.TwoDayAir,
  [UpsServices.Ground]: UpsDirectServiceCode.Ground,
  [UpsServices.ThreeDaySelect]: UpsDirectServiceCode.ThreeDaySelect,
  [UpsServices.NextDayAirSaver]: UpsDirectServiceCode.NextDayAirSaver,
  [UpsServices.NextDayAirEarlyAM]: UpsDirectServiceCode.NextDayAirEarly,
  [UpsServices.SecondDayAirAM]: UpsDirectServiceCode.TwoDayAirAM,
  [UpsServices.UPSStandard]: UpsDirectServiceCode.Standard,
  [UpsServices.WorldWideSaver]: UpsDirectServiceCode.Saver,
  [UpsServices.WorldWideExpress]: UpsDirectServiceCode.WorldwideExpress,
  [UpsServices.WorldWideExpedited]: UpsDirectServiceCode.WorldwideExpedited,
  [UpsServices.WorldWideExpressPlus]: UpsDirectServiceCode.WorldwideExpressPlus,
  [UpsServices.SurePostOver1Lb]: UpsDirectServiceCode.SurePostOver1Lb,
  [UpsServices.SurePostUnder1Lb]: UpsDirectServiceCode.SurePostUnder1Lb,
};

// How many days in the future we can set the label date to
export const CarrierLabelDateRestrictions: Record<Carriers, number | null> = {
  [Carriers.FEDEX]: 10,
  [Carriers.FEDEX_SMART_POST]: 10,
  // User can send up to 7 days in the future with current date as day zero. Format: YYYYMMDD
  // https://developer.ups.com/tag/Shipping?loc=en_US#operation/Shipment
  [Carriers.UPS]: 7,
  [Carriers.UPS_SUREPOST]: 7,
  // User can send up to 30 days in the future
  // https://docs.myibservices.com/?version=latest#5fee3492-d543-447d-9299-e4ce5eb373d7
  [Carriers.USPS]: 30,
  [Carriers.USPS_SHIP]: 14,
  [Carriers.DHL_EXPRESS]: null,
  [Carriers.VEHO]: null,
  [Carriers.APC]: null,
  [Carriers.AMAZON]: null,
  [Carriers.GLS]: null,
  [Carriers.ASENDIA]: null,
  [Carriers.SPEE_DEE]: null,
  [Carriers.PASSPORT_GLOBAL]: null,
  [Carriers.LANDMARK_GLOBAL]: null,
  [Carriers.ETA_EXPRESS]: null,
  [Carriers.EPOST_GLOBAL]: null,
  [Carriers.DHL_ECS]: null,
  [Carriers.FIRST_MILE]: null,
  [Carriers.AUSTRALIA_POST]: null,
  [Carriers.CANADA_POST]: null,
  [Carriers.ROYAL_MAIL_V3]: null,
  [Carriers.USPS_RETURNS]: null,
  [Carriers.CROSSBORDERLY]: null,
};

export function exceedsCarrierLabelDateRestriction(
  fulfillmentDate: Date,
  carrier: Carriers,
): boolean {
  const maxDate = maxCarrierLabelDate(carrier);
  if (!maxDate) {
    return false;
  }
  return fulfillmentDate.getTime() > maxDate.getTime();
}

export function maxCarrierLabelDate(carrier: Carriers): Date | null {
  const maxDays = CarrierLabelDateRestrictions[carrier] ?? null;
  if (!maxDays) {
    return null;
  }
  const maxDaysMs = maxDays * ONE_DAY_MS;
  return new Date(Date.now() + maxDaysMs);
}

export function getClampedLabelDate(
  fulfillmentDate: Date,
  carrier: Carriers,
): Date {
  if (exceedsCarrierLabelDateRestriction(fulfillmentDate, carrier)) {
    const maxDate = maxCarrierLabelDate(carrier);
    if (maxDate) {
      return maxDate;
    }
  }

  return fulfillmentDate;
}

const UPS_TRADEMARK_TEXT =
  "UPS, the UPS brandmark, UPS Ready®, and the color brown are trademarks of United Parcel Service of America, Inc. All Rights Reserved.";

const FEDEX_TRADEMARK_TEXT =
  "The FedEx service marks are owned by Federal Express Corporation and are used by permission.";

export const carrierToTrademarkText: Record<Carriers, string | null> = {
  [Carriers.FEDEX]: FEDEX_TRADEMARK_TEXT,
  [Carriers.FEDEX_SMART_POST]: FEDEX_TRADEMARK_TEXT,
  [Carriers.UPS]: UPS_TRADEMARK_TEXT,
  [Carriers.UPS_SUREPOST]: UPS_TRADEMARK_TEXT,
  [Carriers.USPS]: null,
  [Carriers.USPS_SHIP]: null,
  [Carriers.DHL_EXPRESS]: null,
  [Carriers.VEHO]: null,
  [Carriers.APC]: null,
  [Carriers.AMAZON]: null,
  [Carriers.GLS]: null,
  [Carriers.ASENDIA]: null,
  [Carriers.SPEE_DEE]: null,
  [Carriers.PASSPORT_GLOBAL]: null,
  [Carriers.LANDMARK_GLOBAL]: null,
  [Carriers.ETA_EXPRESS]: null,
  [Carriers.EPOST_GLOBAL]: null,
  [Carriers.DHL_ECS]: null,
  [Carriers.FIRST_MILE]: null,
  [Carriers.AUSTRALIA_POST]: null,
  [Carriers.CANADA_POST]: null,
  [Carriers.ROYAL_MAIL_V3]: null,
  [Carriers.USPS_RETURNS]: null,
  [Carriers.CROSSBORDERLY]: null,
};
